import { EpubNavigator } from "@readium/navigator";
import { Manifest, Publication, Locator, LocatorLocations, LocatorText } from "@readium/shared";
import { HttpFetcher } from "@readium/shared";
import { Link } from "@readium/shared";
import * as Sentry from '@sentry/browser';
import ContentEncryptionService from '../../../lib/ContentEncryptionService.js';
import Decrypter from '../../../lib/Decrypter.js';
import fixXhtmlMarkup from './fixXhtmlMarkup.js';
import Mark from 'mark.js/dist/mark.js';
import hexToRgba from '_shared_/js/utils/hexToRgba.js';
import { getCssSelector } from 'css-selector-generator'; // @R2 lazy migration
import { ReadiumFadeAnimator } from '../ReadiumFadeAnimator.js';
import { PagebreakTracker } from '../PagebreakTracker.js';
import { isAndroid } from '../../../../utils/platform.js';

// Import CSS styles and font size configuration
import {
    FONT_SIZE_MIN,
    FONT_SIZE_MAX,
    FONT_SIZE_STEP,
    FONT_SIZE_DEFAULT,
    API_FONT_SIZE_BASE,
    API_FONT_SIZE_MIN,
    API_FONT_SIZE_MAX,
    SCROLL_MODE_MARGINS_CSS,
    COVER_FRAME_CSS,
    getAccessibleFontsCss,
    MARGINS_DEFAULT,
} from './readium2-styles.js';

// Re-export font size constants for external use
export { FONT_SIZE_MIN, FONT_SIZE_MAX, API_FONT_SIZE_BASE, API_FONT_SIZE_MIN, API_FONT_SIZE_MAX };

/**
 * Adds a Sentry breadcrumb scoped under the `readium.*` category namespace.
 * @param {string} category - sub-category appended to `readium.`
 * @param {string} message - short event identifier
 * @param {Object} [data] - non-PII context fields
 * @param {'info'|'warning'|'error'} [level]
 */
function sendBreadcrumb(category, message, data = {}, level = 'info') {
    Sentry.addBreadcrumb({
        category: `readium.${category}`,
        message,
        level,
        data,
    });
}

// Readium's FXLFramePoolManager rejects offscreen-load watchdog promises with this prefix.
const OFFSCREEN_TIMEOUT_PREFIX = 'Offscreen load timeout:';

/**
 * Returns true if a rejection reason is a Readium offscreen-load timeout string.
 * @param {unknown} reason - The rejection reason to test
 * @returns {boolean}
 */
const isOffscreenTimeoutRejection = (reason) =>
    typeof reason === 'string' && reason.startsWith(OFFSCREEN_TIMEOUT_PREFIX);

/**
 * Creates a Readium Locator from epubR2Location data for navigation.
 * Applies workaround for @readium/navigator bug that ignores progression === 0.
 * @param {Object} epubR2Location - Location data with href, type, and locations
 * @returns {Locator} Readium Locator instance ready for navigation
 */
export function createR2Locator(epubR2Location) {
    const locatorData = { ...epubR2Location };

    // Workaround: @readium/navigator ignores progression === 0
    // Bug in EpubNavigator.ts:758 - condition `progression > 0` excludes 0
    if (locatorData.locations?.progression === 0) {
        locatorData.locations = { ...locatorData.locations, progression: 0.0001 };
    }

    // Pass text.highlight to LocatorText so go() can locate the text in the resource
    if (locatorData.text) {
        locatorData.text = new LocatorText({ highlight: locatorData.text });
    }

    return new Locator(locatorData);
}

function parseCFI(cfi) {
    const parts = cfi.split('/').filter(p => p);

    return {
        spineIndex: parseInt(parts[0]) - 1,
        elementPath: parts.slice(1),
        textOffset: parts[parts.length - 1].split(':')
    };
}

// ─── CSS utility functions (shared by EpubViewer and DecryptingResource) ─────

/**
 * Extract the directory portion of a path (trailing slash included).
 * e.g. "OEBPS/styles/main.css" → "OEBPS/styles/"
 *      "main.css"              → ""
 */
function dirFromPath(path) {
    const i = path.lastIndexOf('/');
    return i >= 0 ? path.substring(0, i + 1) : '';
}

/**
 * Find a CSS entry in a cache Map by a relative href.
 * Returns { css, cacheKey } or null.
 *
 * Cache keys are full publication paths (e.g. "OEBPS/styles/main.css")
 * but <link> hrefs can be relative (e.g. "../styles/main.css").
 * Resolves the mismatch via:
 * 1. Direct / normalized match  2. Suffix match  3. Filename-only match
 */
function findCssInCacheWithKey(cache, relativeHref) {
    const normalized = relativeHref.replace(/^(\.\.\/)+/, '').replace(/^\.\//, '');

    // Direct match
    if (cache.has(relativeHref)) return { css: cache.get(relativeHref), cacheKey: relativeHref };
    if (cache.has(normalized)) return { css: cache.get(normalized), cacheKey: normalized };

    // Suffix match: "OEBPS/styles/main.css" ends with "/styles/main.css"
    for (const [key, val] of cache) {
        if (key.endsWith('/' + normalized) || normalized.endsWith('/' + key)) return { css: val, cacheKey: key };
    }

    // Filename-only match
    const filename = normalized.split('/').pop();
    for (const [key, val] of cache) {
        if (key.endsWith('/' + filename) || key === filename) return { css: val, cacheKey: key };
    }

    return null;
}

/**
 * Resolve a relative path against a base directory.
 * e.g. resolveRelativePath("OEBPS/css/", "../fonts/f0.otf") → "OEBPS/fonts/f0.otf"
 */
function resolveRelativePath(baseDir, relativePath) {
    const parts = baseDir.split('/').filter(Boolean);

    for (const segment of relativePath.split('/')) {
        if (segment === '..') parts.pop();
        else if (segment !== '.' && segment !== '') parts.push(segment);
    }

    return parts.join('/');
}

/**
 * Make a publication-absolute path relative to a given directory.
 * e.g. makeRelativePath("OEBPS/", "OEBPS/fonts/f0.otf") → "fonts/f0.otf"
 */
function makeRelativePath(fromDir, toPath) {
    const fromParts = fromDir.split('/').filter(Boolean);
    const toParts = toPath.split('/').filter(Boolean);

    let common = 0;
    while (common < fromParts.length && common < toParts.length &&
           fromParts[common] === toParts[common]) {
        common++;
    }

    const ups = fromParts.length - common;
    const remaining = toParts.slice(common);
    return [...Array(ups).fill('..'), ...remaining].join('/') || '.';
}

/**
 * Rebase relative url() references in CSS so they resolve correctly
 * when the CSS is inlined into an XHTML file at a different path.
 *
 * e.g. CSS at "OEBPS/css/FontSrc.css" with url('../fonts/f0.otf')
 *      inlined into "OEBPS/cover.xhtml"
 *      → url('fonts/f0.otf') (relative to OEBPS/)
 */
function rebaseCssUrls(css, cssAbsPath, xhtmlDir) {
    const cssDir = dirFromPath(cssAbsPath);

    if (cssDir === xhtmlDir) return css;

    return css.replace(/url\(\s*(['"]?)([^'")]+)\1\s*\)/gi, (match, quote, rawUrl) => {
        if (rawUrl.startsWith('data:') || rawUrl.startsWith('http://') ||
            rawUrl.startsWith('https://') || rawUrl.startsWith('//') ||
            rawUrl.startsWith('blob:')) {
            return match;
        }

        const absPath = resolveRelativePath(cssDir, rawUrl);
        const rebasedUrl = makeRelativePath(xhtmlDir, absPath);

        return `url(${quote}${rebasedUrl}${quote})`;
    });
}

/**
 * Resolve @import statements in cached CSS by inlining the imported CSS content
 * from the cache. This prevents the browser from trying to fetch @import URLs
 * (which point to encrypted/inaccessible resources) when rendering inline <style> tags.
 *
 * Handles all @import formats:
 *   @import "file.css";
 *   @import 'file.css';
 *   @import url("file.css");
 *   @import url('file.css');
 *   @import url(file.css);
 *   @import "file.css" <media-query>;
 *
 * Only resolves one level of imports. A snapshot of the original cache is used
 * for reads so that cache mutations during iteration don't affect sibling
 * imports in the same pass.
 */
function resolveImportsInCssCache(cache) {
    const snapshot = new Map(cache);

    for (const [cacheKey, cssText] of snapshot) {
        if (!cssText.includes('@import')) continue;

        const importerDir = dirFromPath(cacheKey);
        const decommented = cssText.replace(/\/\*[\s\S]*?\*\//g, '');
        const importRegex = /@import\s+(?:url\(\s*(['"]?)([^'")]+)\1\s*\)|(['"])([^'"]+)\3)\s*([^;]*);\s*/gi;

        const resolved = decommented.replace(importRegex, (match, _urlQuote, urlPath, _strQuote, strPath, mediaQuery) => {
            const importPath = urlPath || strPath;
            if (!importPath) return match;

            // Strip query and fragment so versioned imports (e.g. "x.css?v=2") pass the extension check
            const pathWithoutQuery = importPath.split('?')[0].split('#')[0];
            if (!pathWithoutQuery.endsWith('.css')) return match;

            const absoluteImportPath = resolveRelativePath(importerDir, pathWithoutQuery);
            const result = findCssInCacheWithKey(snapshot, absoluteImportPath);
            if (!result) {
                console.warn('Imported CSS not found in cache:', importPath, 'from', cacheKey);
                return match;
            }

            const rebasedCss = rebaseCssUrls(result.css, result.cacheKey, importerDir);
            const trimmedMedia = mediaQuery?.trim();
            if (trimmedMedia) {
                return `@media ${trimmedMedia} {\n${rebasedCss}\n}\n`;
            }

            return rebasedCss + '\n';
        });

        if (resolved !== cssText) {
            cache.set(cacheKey, resolved);
        }
    }
}

// ─── BEGIN: IDPF Font Deobfuscation ──────────────────────────────────────────
// Self-contained module to support EPUB font obfuscation per the IDPF 2008
// spec (http://www.idpf.org/2008/embedding) and Adobe RC obfuscation
// (http://ns.adobe.com/pdf/enc#RC).
//
// Flow:
//   1. Read META-INF/encryption.xml to discover obfuscated resources.
//   2. Read the OPF and extract the publication's unique-identifier.
//   3. Derive the obfuscation key (SHA-1 for IDPF, UUID bytes for Adobe).
//   4. Fetch each obfuscated font, XOR the prefix bytes back to plain.
//   5. Expose each deobfuscated font as a blob: URL and rewrite any
//      url(...) references in the cached CSS to point to those blob URLs
//      (the iframe browser fetches fonts via HTTP, bypassing JS fetchers,
//      so blob URL substitution is required).
//
// To remove this feature: delete this section and the
// setupIdpfFontDeobfuscation() call inside EpubViewer.load() (and the
// matching cleanup inside EpubViewer.destroy()).

const IDPF_OBFUSCATION_ALGORITHM = 'http://www.idpf.org/2008/embedding';
const ADOBE_OBFUSCATION_ALGORITHM = 'http://ns.adobe.com/pdf/enc#RC';
const IDPF_OBFUSCATION_PREFIX_LEN = 1040; // 52 × 20-byte SHA-1
const ADOBE_OBFUSCATION_PREFIX_LEN = 1024; // 64 × 16-byte UID

/**
 * Parse META-INF/encryption.xml and return entries whose algorithm matches
 * a supported font obfuscation scheme. Hrefs are decoded from URI form and
 * are publication-relative (relative to the OCF root).
 *
 * @param {string} xmlText
 * @returns {Array<{href: string, algorithm: string}>}
 */
function parseEncryptionXmlForObfuscation(xmlText) {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlText, 'application/xml');
    if (xmlDoc.querySelector('parsererror')) return [];

    const supported = new Set([IDPF_OBFUSCATION_ALGORITHM, ADOBE_OBFUSCATION_ALGORITHM]);
    const results = [];
    const encryptedDataNodes = xmlDoc.getElementsByTagNameNS('*', 'EncryptedData');

    for (const node of encryptedDataNodes) {
        const method = node.getElementsByTagNameNS('*', 'EncryptionMethod')[0];
        const cipherRef = node.getElementsByTagNameNS('*', 'CipherReference')[0];
        if (!method || !cipherRef) continue;

        const algorithm = method.getAttribute('Algorithm');
        if (!supported.has(algorithm)) continue;

        const uri = cipherRef.getAttribute('URI');
        if (!uri) continue;

        let href;
        try {
            href = decodeURIComponent(uri);
        } catch {
            href = uri;
        }
        results.push({ href, algorithm });
    }
    return results;
}

/**
 * Extract the publication's unique-identifier value from the OPF document.
 * Resolves package@unique-identifier to its matching <dc:identifier> element.
 * Falls back to the first <dc:identifier> if the IDREF can't be resolved.
 *
 * @param {string} opfText
 * @returns {string|null}
 */
function extractOpfUniqueIdentifier(opfText) {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(opfText, 'application/xml');
    if (xmlDoc.querySelector('parsererror')) return null;

    const pkg = xmlDoc.getElementsByTagNameNS('*', 'package')[0];
    if (!pkg) return null;

    const idRef = pkg.getAttribute('unique-identifier');
    const identifiers = xmlDoc.getElementsByTagNameNS('*', 'identifier');

    let target = null;
    if (idRef) {
        for (const id of identifiers) {
            if (id.getAttribute('id') === idRef) { target = id; break; }
        }
    }
    if (!target && identifiers.length > 0) target = identifiers[0];
    return target ? target.textContent : null;
}

/**
 * Compute the IDPF obfuscation key: SHA-1 of the unique identifier with
 * whitespace stripped (U+0020, U+0009, U+000D, U+000A), as a 20-byte
 * Uint8Array.
 *
 * @param {string} identifier
 * @returns {Promise<Uint8Array>}
 */
async function computeIdpfFontKey(identifier) {
    const stripped = identifier.replace(/[\u0020\u0009\u000D\u000A]/g, '');
    const buf = new TextEncoder().encode(stripped);
    const digest = await crypto.subtle.digest('SHA-1', buf);
    return new Uint8Array(digest);
}

/**
 * Compute the Adobe obfuscation key: parse the unique identifier as a UUID
 * and return its 16 raw bytes. Returns null if the identifier isn't a
 * recognizable UUID hex string.
 *
 * @param {string} identifier
 * @returns {Uint8Array|null}
 */
function computeAdobeFontKey(identifier) {
    const hex = identifier.replace(/^urn:uuid:/i, '').replace(/-/g, '');
    if (!/^[0-9a-fA-F]{32}$/.test(hex)) return null;
    const key = new Uint8Array(16);
    for (let i = 0; i < 16; i++) {
        key[i] = parseInt(hex.substr(i * 2, 2), 16);
    }
    return key;
}

/**
 * XOR the first prefixLength bytes of the input with a cycled key.
 * Returns a new Uint8Array containing the deobfuscated content; the
 * tail (beyond prefixLength) is copied unchanged.
 *
 * @param {Uint8Array} input
 * @param {Uint8Array} key
 * @param {number} prefixLength
 * @returns {Uint8Array}
 */
function deobfuscateFontBytes(input, key, prefixLength) {
    const output = new Uint8Array(input.length);
    output.set(input);

    const limit = Math.min(prefixLength, output.length);
    for (let i = 0; i < limit; i++) {
        output[i] = input[i] ^ key[i % key.length];
    }
    return output;
}

/**
 * Build a lookup map keyed by lowercased filename so url() rewrites can
 * recover from path mismatches (different folder roots, absolute paths,
 * mixed-case extensions like .TTF vs .ttf, etc.).
 *
 * @param {Map<string, string>} fontBlobUrls
 * @returns {Map<string, string>}
 */
function buildFontFilenameIndex(fontBlobUrls) {
    const index = new Map();
    for (const [href, blobUrl] of fontBlobUrls) {
        const filename = href.split('/').pop().toLowerCase();
        if (filename) index.set(filename, blobUrl);
    }
    return index;
}

/**
 * Resolve a url() target against the deobfuscated font map. Tries:
 *   1. exact publication-relative path match (after relative resolution)
 *   2. raw stripped-leading-slash match (handles absolute paths)
 *   3. filename-only match (case-insensitive) — needed because authors
 *      mix relative/absolute/OPF-relative paths and case.
 *
 * @returns {string|null}
 */
function lookupFontBlobUrl(cssDir, rawUrl, fontBlobUrls, filenameIndex) {
    const cleanUrl = rawUrl.split('?')[0].split('#')[0];
    const stripped = cleanUrl.replace(/^\//, '');

    const absPath = resolveRelativePath(cssDir, stripped);
    if (fontBlobUrls.has(absPath)) return fontBlobUrls.get(absPath);
    if (fontBlobUrls.has(stripped)) return fontBlobUrls.get(stripped);

    const filename = cleanUrl.split('/').pop().toLowerCase();
    return filenameIndex.get(filename) ?? null;
}

/**
 * Replace any url(...) reference in the cached CSS that points to a
 * deobfuscated font with its blob URL. Mutates the cache in place.
 *
 * @param {Map<string, string>} cssCache
 * @param {Map<string, string>} fontBlobUrls — publication-relative href → blob URL
 */
function rewriteFontUrlsInCssCache(cssCache, fontBlobUrls) {
    if (fontBlobUrls.size === 0) return;

    const filenameIndex = buildFontFilenameIndex(fontBlobUrls);

    for (const [cacheKey, cssText] of cssCache) {
        const cssDir = dirFromPath(cacheKey);
        const rewritten = cssText.replace(
            /url\(\s*(['"]?)([^'")]+)\1\s*\)/gi,
            (match, quote, rawUrl) => {
                if (rawUrl.startsWith('data:') || rawUrl.startsWith('blob:') ||
                    rawUrl.startsWith('http://') || rawUrl.startsWith('https://') ||
                    rawUrl.startsWith('//')) {
                    return match;
                }
                const blobUrl = lookupFontBlobUrl(cssDir, rawUrl, fontBlobUrls, filenameIndex);
                if (!blobUrl) return match;
                return `url(${quote}${blobUrl}${quote})`;
            }
        );
        if (rewritten !== cssText) cssCache.set(cacheKey, rewritten);
    }
}

/**
 * Replace url(...) font references in arbitrary text (HTML or CSS) with
 * blob URLs from the deobfuscated font map. Used by DecryptingResource
 * to cover @font-face declarations that live in inline <style> blocks
 * inside XHTML documents (which never enter the CSS cache).
 *
 * @param {string} text
 * @param {Map<string, string>} fontBlobUrls
 * @param {string} baseDir — publication-relative directory of the document
 * @returns {string}
 */
function rewriteFontUrlsInText(text, fontBlobUrls, baseDir) {
    if (!fontBlobUrls || fontBlobUrls.size === 0) return text;
    const filenameIndex = buildFontFilenameIndex(fontBlobUrls);
    return text.replace(
        /url\(\s*(['"]?)([^'")]+)\1\s*\)/gi,
        (match, quote, rawUrl) => {
            if (rawUrl.startsWith('data:') || rawUrl.startsWith('blob:') ||
                rawUrl.startsWith('http://') || rawUrl.startsWith('https://') ||
                rawUrl.startsWith('//')) {
                return match;
            }
            const blobUrl = lookupFontBlobUrl(baseDir, rawUrl, fontBlobUrls, filenameIndex);
            if (!blobUrl) return match;
            return `url(${quote}${blobUrl}${quote})`;
        }
    );
}

/**
 * Best-effort font MIME type lookup based on file extension.
 * Used so the resulting blob URLs carry an appropriate Content-Type.
 *
 * @param {string} href
 * @returns {string}
 */
function guessFontMimeType(href) {
    const lower = href.toLowerCase();
    if (lower.endsWith('.otf')) return 'font/otf';
    if (lower.endsWith('.ttf')) return 'font/ttf';
    if (lower.endsWith('.woff')) return 'font/woff';
    if (lower.endsWith('.woff2')) return 'font/woff2';
    console.warn('[IDPF font deobfuscation] Unrecognized font extension, falling back to octet-stream:', href);
    return 'application/octet-stream';
}

/**
 * Read a resource as a string through the publication fetcher. Goes through
 * the full DecryptingResource pipeline so that publica.la-encrypted OCF
 * metadata (encryption.xml, container.xml — matched by the .xml filter in
 * ContentEncryptionService.shouldDecryptFile) is transparently decrypted.
 *
 * @param {Object} fetcher
 * @param {string} href
 * @returns {Promise<string>}
 */
function readResourceAsString(fetcher, href) {
    return fetcher.get(new Link({ href })).readAsString();
}

/**
 * Extract the OPF path from container.xml. Standalone helper so the IDPF
 * section doesn't depend on EpubViewer.getOpfPathFromContainer (which uses
 * the decrypting fetcher and would also need the bypass).
 *
 * @param {string} xmlText
 * @returns {string|null}
 */
function extractOpfPathFromContainerXml(xmlText) {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlText, 'application/xml');
    if (xmlDoc.querySelector('parsererror')) return null;

    const rootfiles = xmlDoc.getElementsByTagNameNS('*', 'rootfile');
    for (const rootfile of rootfiles) {
        if (rootfile.getAttribute('media-type') === 'application/oebps-package+xml') {
            return rootfile.getAttribute('full-path');
        }
    }
    return null;
}

/**
 * Fallback path: read container.xml and the OPF to obtain the unique
 * identifier when the webpub-manifest doesn't expose `metadata.identifier`.
 * Returns null on any failure — callers should treat that as "no identifier
 * available" and abort deobfuscation.
 *
 * @param {Object} fetcher
 * @returns {Promise<string|null>}
 */
async function fetchOpfUniqueIdentifier(fetcher) {
    let containerXmlText;
    try {
        containerXmlText = await readResourceAsString(fetcher, 'META-INF/container.xml');
    } catch (error) {
        console.warn('[IDPF font deobfuscation] Could not fetch container.xml:', error);
        return null;
    }

    const opfPath = extractOpfPathFromContainerXml(containerXmlText);
    if (!opfPath) {
        console.warn('[IDPF font deobfuscation] No OPF path in container.xml');
        return null;
    }

    let opfText;
    try {
        opfText = await readResourceAsString(fetcher, opfPath);
    } catch (error) {
        console.warn('[IDPF font deobfuscation] Could not fetch OPF:', error);
        return null;
    }

    return extractOpfUniqueIdentifier(opfText);
}

/**
 * Top-level orchestrator: reads encryption.xml + container.xml + OPF,
 * deobfuscates each listed font into a blob URL, then rewrites url(...)
 * references in the shared CSS cache so the iframe browser loads the
 * deobfuscated fonts.
 *
 * Designed to run in parallel with preFetchCssFiles(): the CSS rewrite
 * step waits on `cssCacheReady` before mutating the cache, so the
 * encryption.xml / OPF round-trips overlap with the CSS prefetch.
 * In the common case (no encryption.xml) the 404 stays hidden under the
 * CSS prefetch latency.
 *
 * @param {Object} fetcher — the publication fetcher
 * @param {Promise<Map<string, string>>} cssCacheReady — resolves with the
 *        populated CSS cache (the same Map will be mutated in place)
 * @param {string|null} manifestIdentifier — publication unique-identifier as
 *        already exposed in the readium webpub-manifest (`metadata.identifier`).
 *        When present, lets us skip fetching `META-INF/container.xml` and the
 *        OPF entirely (saves 2 round-trips on every book open with obfuscated
 *        fonts). Streamers like go-toolkit and r2-streamer-js populate this
 *        field by extracting the OPF's `<dc:identifier>` referenced by
 *        `package@unique-identifier`, so the value is byte-equivalent to what
 *        we would read from the OPF directly. Null/missing falls back to the
 *        legacy container.xml + OPF path.
 * @returns {Promise<Map<string, string>>} href → blob URL (revoke on cleanup)
 */
async function setupIdpfFontDeobfuscation(fetcher, cssCacheReady, manifestIdentifier = null) {
    const blobUrls = new Map();

    let encryptionXmlText;
    try {
        encryptionXmlText = await readResourceAsString(fetcher, 'META-INF/encryption.xml');
    } catch {
        // No encryption.xml — nothing obfuscated.
        return blobUrls;
    }

    const entries = parseEncryptionXmlForObfuscation(encryptionXmlText);
    if (entries.length === 0) return blobUrls;

    // Prefer the identifier already exposed by the webpub-manifest. Falls
    // back to fetching container.xml + OPF only when the manifest doesn't
    // expose one (custom or legacy streamers).
    let identifier = manifestIdentifier;
    if (!identifier) {
        identifier = await fetchOpfUniqueIdentifier(fetcher);
    }
    if (!identifier) {
        console.warn('[IDPF font deobfuscation] No unique identifier available (manifest or OPF)');
        return blobUrls;
    }

    const idpfKey = await computeIdpfFontKey(identifier);
    const adobeKey = computeAdobeFontKey(identifier);

    // Deobfuscate fonts in parallel — each font is an independent fetch+XOR.
    await Promise.all(entries.map(async ({ href, algorithm }) => {
        let key, prefixLen;
        if (algorithm === IDPF_OBFUSCATION_ALGORITHM) {
            key = idpfKey;
            prefixLen = IDPF_OBFUSCATION_PREFIX_LEN;
        } else if (algorithm === ADOBE_OBFUSCATION_ALGORITHM && adobeKey) {
            key = adobeKey;
            prefixLen = ADOBE_OBFUSCATION_PREFIX_LEN;
        } else {
            return;
        }

        try {
            const fontRes = fetcher.get(new Link({ href }));
            const raw = await fontRes.read();
            if (!raw) {
                console.warn('[IDPF font deobfuscation] Empty response for', href);
                return;
            }
            // Defensive: read() is contractually Uint8Array | undefined, but a
            // future readium version or a custom fetcher wrapper could return
            // a bare ArrayBuffer. output.set() would still work, but element
            // access (input[i]) would yield undefined → NaN → 0, silently
            // leaving the font obfuscated. Normalize to Uint8Array.
            const obfuscatedBytes = raw instanceof Uint8Array ? raw : new Uint8Array(raw);
            const deobfuscated = deobfuscateFontBytes(obfuscatedBytes, key, prefixLen);
            const blobUrl = URL.createObjectURL(
                new Blob([deobfuscated], { type: guessFontMimeType(href) })
            );
            blobUrls.set(href, blobUrl);
        } catch (error) {
            console.warn('[IDPF font deobfuscation] Failed for', href, error);
        }
    }));

    // Rewriting must happen after the CSS cache has been populated.
    const cssCache = await cssCacheReady;
    rewriteFontUrlsInCssCache(cssCache, blobUrls);
    return blobUrls;
}
// ─── END: IDPF Font Deobfuscation ────────────────────────────────────────────

/**
 * EpubViewer - A comprehensive EPUB reader implementation using Readium 2
 *
 * This class provides a complete EPUB reading experience with support for:
 * - Content decryption
 * - Multiple layout modes (single, double, scroll)
 * - Text selection and highlighting
 * - Chapter navigation and boundaries detection
 * - Theme management
 * - Keyboard and touch interactions
 * - Preview mode restrictions
 *
 * Built on top of Readium 2 Navigator and follows Readium 2 architecture patterns.
 * Handles both encrypted and unencrypted EPUB content through a custom fetcher system.
 *
 * @class EpubViewer
 */
export class EpubViewer {
    /**
     * Creates an instance of EpubViewer with callback configurations
     *
     * @param {Object} config - Configuration object for the EPUB viewer
     */
    constructor({
        isPreviewMode,
        settings={},
        lastPosition={},
        onGoNext=()=>{},
        onGoPrev=()=>{},
        onKeyboardArrowPressed=()=>{},
        onEndOfChapter=()=>{},
        onStartOfChapter=()=>{},
        onChapterChange=()=>{},
        onClearSelection=()=>{},
        onSelectionInProgress=()=>{},
        onToggleUi=()=>{},
        onSelectHighlight=()=>{},
        onChangePosition=()=>{},
        onLoadFrame=()=>{},
        onNavigationStart=()=>{},
        onNavigationEnd=()=>{},
        onHighlightCreated=()=>{},
        onHighlightUpdated=()=>{},
        onHighlightDeleted=()=>{},
        onSearchHighlightRendered=()=>{},
        onTrackEvent=()=>{},
        onSetCurrentHighlight=()=>{},
        onFulfillQueryWordsHighlight=()=>{},
        onHandleAnonymousSessionSelections=()=>{},
        onImageClick=()=>{},
        isKeyboardNavigationEnabled=()=>true,
        onSwipe=()=>{},
        onPagebreakConfirmed=()=>{},
        pageViewConfirmDelayMs,
    }) {
        this.epubNavigator = null;
        this.publication = null;
        this.lastPosition = lastPosition;
        this.currentFontSize = this.parseInitialFontSize(settings?.zoom);
        this.settings = this.parseSettings(settings);
        this.preventClick = true;
        this.selection = {};
        this.currentChapterHref = null;
        this.isChapterEnd = false;
        this.isChapterStart = false;
        this.isPreviewMode = isPreviewMode;
        this.onGoNext = onGoNext;
        this.onGoPrev = onGoPrev;
        this.onKeyboardArrowPressed = onKeyboardArrowPressed;
        this.onEndOfChapter = onEndOfChapter;
        this.onStartOfChapter = onStartOfChapter;
        this.onChapterChange = onChapterChange;
        this.onClearSelection = onClearSelection;
        this.onSelectionInProgress = onSelectionInProgress;
        this.onToggleUi = onToggleUi;
        this.onSelectHighlight = onSelectHighlight;
        this.onChangePosition = onChangePosition;
        this.onLoadFrame = onLoadFrame;
        this.onNavigationStart = onNavigationStart;
        this.onNavigationEnd = onNavigationEnd;
        this.onImageClick = onImageClick;
        this.isKeyboardNavigationEnabled = isKeyboardNavigationEnabled;
        this.onSwipe = onSwipe;
        this._isFxlLoading = () => false;
        this._fadeAnimator = new ReadiumFadeAnimator();

        // Initialize HighlightManager with callbacks
        this.highlightManager = new HighlightManager({
            onHighlightCreated,
            onHighlightUpdated,
            onHighlightDeleted,
            onSearchHighlightRendered,
            onTrackEvent,
            onSetCurrentHighlight,
            onFulfillQueryWordsHighlight,
            onHandleAnonymousSessionSelections,
            onSelectHighlight: this.onSelectHighlight,
            onSelectionInProgress, // @R2 selection popup
            onClearSelection, // @R2 dismiss selection popup
        });

        this._pagebreakTracker = new PagebreakTracker(onPagebreakConfirmed, {
            confirmDelayMs: pageViewConfirmDelayMs,
        });

        // Store decrypted CSS content to avoid re-fetching on each frame load
        this.decryptedCssCache = new Map();

        // Container element reference for viewport manipulation
        this.container = null;

        // Flag to suppress animation only during layout changes and sidebar reflow
        this._suppressAnimation = false;
    }

    /**
     * Checks whether the last available page is currently visible in the navigator's viewport.
     * Works for FXL (single/double), reflowable, and preview EPUBs by comparing
     * visible position numbers against the last available position.
     * @returns {boolean} True if the last available page is visible
     */
    isLastPageVisible() {
        const viewportPositions = this.epubNavigator?.viewport?.positions;
        const maxVisiblePosition = viewportPositions?.length ? Math.max(...viewportPositions) : 0;
        const lastAvailablePosition = this.epubNavigator?.positions?.[this.epubNavigator.positions.length - 1]?.locations?.position;
        return lastAvailablePosition > 0 && maxVisiblePosition >= lastAvailablePosition;
    }

    /**
     * Parses the initial font size from stored settings.
     * Handles both API format (90-480) and REM format (0.75-4).
     * @param {number|null|undefined} zoom - Stored zoom value
     * @returns {number} Font size in rem
     */
    parseInitialFontSize(zoom) {
        if (typeof zoom !== 'number' || zoom <= 0) {
            return FONT_SIZE_DEFAULT;
        }
     
        if (zoom >= API_FONT_SIZE_MIN && zoom <= API_FONT_SIZE_MAX) {
            return zoom / API_FONT_SIZE_BASE;
        }
        
        if (zoom >= FONT_SIZE_MIN && zoom <= FONT_SIZE_MAX) {
            return zoom;
        }

        return FONT_SIZE_DEFAULT;
    }

    get scrollPageMode() {
        return this.epubNavigator?.settings?.scroll || this.settings?.scroll;
    }

    /**
     * EPUB layout configurations for different reading modes
     */
    epubLayouts = {
        single: {
            scroll: 'auto',
            syntheticSpread: 'single',
        },
        scroll: {
            scroll: 'scroll-doc',
            syntheticSpread: 'auto',
        },
        double: {
            scroll: 'auto',
            syntheticSpread: 'auto',
        },
    };

    /**
     * Readium 2 Navigator event listeners configuration
     */
    listeners = {
        frameLoaded: async (_wnd) => {
            // WCAG 2.4.1: Set iframe title attribute for screen readers.
            // Uses chapter title from TOC when available, falls back to document title.
            const iframeElement = _wnd.frameElement;

            if (iframeElement) {
                const chapTitle = this.publication.toc?.items?.find(el =>
                    el.href.includes(this.epubNavigator?.currentLocation?.href))
                const docTitle = _wnd.document.title;
                iframeElement.setAttribute('title', chapTitle || docTitle);
            }

            // WCAG 3.1.2: Set content language from publication metadata.
            const pubLang = this.publication.metadata?.languages;
            if (pubLang) {
                const lang = Array.isArray(pubLang) ? pubLang[0] : pubLang;
                _wnd.document.documentElement.lang = lang;
            }

            // Guard: ensure the css-selector-generator injectable is available.
            // On the very first page load a race condition can leave the injectable
            // uninitialised, causing "getCssSelector is not a function" on click.
            if (!_wnd._readium_cssSelectorGenerator) {
                _wnd._readium_cssSelectorGenerator = {
                    getCssSelector: (el) => {
                        try { return el?.tagName?.toLowerCase() || ''; } catch { return ''; }
                    }
                };
            }

            // Add custom annotation class for showing the icon in highlights
            const head = _wnd.document.head;
            const annotationStyle = _wnd.document.createElement('style');
            annotationStyle.innerText = `mark{position:relative}mark.annotation-icon:after{content:'';width:45px;height:30px;background:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'%3E%3Cpath d='M0 0h24v24H0z' fill='none'/%3E%3Cpath d='M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 14h-3v3h-2v-3H8v-2h3v-3h2v3h3v2zm-3-7V3.5L18.5 9H13z'/%3E%3C/svg%3E") no-repeat center;background-size:contain;position:absolute;opacity:0.2;left:-13px;top:-30px;transform:scale(0.85);transition:all 150ms ease}mark.annotation-icon:hover:after{opacity:0.9;transform:scale(1)}@media (hover: none){mark.annotation-icon:after{opacity:0.85;transform:scale(1)}} @media print { * { display: none !important; } }`;
            head.appendChild(annotationStyle);

            _wnd.document.addEventListener('contextmenu', (e) => {
                this.preventContextMenu(e);
            });

            _wnd.document.addEventListener('keyup', (e) => {
                try {
                    this.handleKeyboardArrowKeys(e);
                } catch (error) {
                    console.warn('Error handling keyboard event:', error);
                }
            });

            _wnd.document.body.style.webkitTouchCallout = 'none';
            _wnd.document.querySelectorAll('img').forEach(img => img.setAttribute('draggable', 'false'));

            // R2's ColumnSnapper clears selection on touchmove. Block it when selection exists.
            if (!this.scrollPageMode) {
                _wnd.document.addEventListener('touchmove', (e) => {
                    const selection = _wnd.getSelection();
                    if (selection && selection.toString().length > 0) {
                        e.stopPropagation();
                    }
                }, { capture: true, passive: false });
            }

            _wnd.addEventListener('beforeprint', () => {
                const iframe = _wnd.document;
                if (iframe.getSelection) {
                    iframe.getSelection().removeAllRanges();
                } else if (iframe.selection) {
                    iframe.selection.empty();
                }
            });

            // Cover / first frame: the CSS inlining step (inlineCssInHtml) may
            // succeed at string level, but R2's DOMParser + injectForDocument
            // pipeline can strip or fail to retain our <style> tags for certain
            // frames (e.g. the cover which uses a different XML namespace).
            // When this happens, original <link href="../stylesheet.css"> tags
            // remain — they can't resolve from blob: URLs and produce no styles.
            // → We detect non-blob <link> stylesheet tags and inject the CSS
            // from our decryptedCssCache directly into the DOM.
            try {
                const doc = _wnd.document;

                // Determine the XHTML item href from the <base> tag Readium injects
                const baseEl = doc.querySelector('base[data-readium]');
                const xhtmlDir = this._dirFromBaseHref(baseEl?.href);

                // Inject CSS for broken (non-blob) <link> stylesheet tags
                const cssCache = this.decryptedCssCache;
                const links = [...doc.querySelectorAll('link[rel="stylesheet"]')];
                for (const link of links) {
                    const href = link.getAttribute('href') || '';
                    // Blob links are R2's own CSS — leave them alone
                    if (href.startsWith('blob:')) continue;
                    // This is an EPUB CSS link that wasn't inlined — try to inject from cache
                    if (cssCache && cssCache.size > 0) {
                        const result = findCssInCacheWithKey(cssCache, href);
                        if (result) {
                            let css = result.css;
                            // Rebase url() references for the XHTML context
                            if (xhtmlDir !== null) {
                                css = rebaseCssUrls(css, result.cacheKey, xhtmlDir);
                            }
                            const style = doc.createElement('style');
                            style.setAttribute('type', 'text/css');
                            style.setAttribute('data-original-href', href);
                            style.textContent = css;
                            link.parentNode.replaceChild(style, link);
                        }
                    }
                }
            } catch(e) {
                console.warn('[R2] Error fixing EPUB CSS in iframe:', e.message);
            }

            /**
             * CSS injected into cover frames to fix image rendering issues.
             *
             * FIX: This is a workaround for a known bug in ReadiumCSS where cover images
             * are clipped or not displayed correctly. This fix can be removed if/when
             * the Readium team implements a native solution upstream.
             *
             * ## Problem
             * ReadiumCSS applies vertical margins to the body element via `--RS__pageGutter`
             * (typically 10-50px) to provide breathing room for text content. However, when
             * a cover page uses relative height (e.g., `height: 99vh` or `height: 100%`),
             * these margins cause the image to overflow the viewport:
             *
             *   99vh (image) + 50px (margin-top) + 50px (margin-bottom) > 100vh
             *
             * This results in:
             * - Image not visible on first load (race condition with CSS injection)
             * - Image partially clipped after navigation
             * - Only a small portion of the cover visible
             *
             * The issue affects desktop web only. iOS/Android use native WebViews with
             * different Readium implementations that don't have this problem.
             *
             * ## Solution
             * 1. Detect cover frames using `_isCoverFrame()` in the frameLoaded listener
             * 2. Inject this CSS to remove margins and use flexbox centering
             * 3. Apply inline styles for maximum specificity over ReadiumCSS
             *
             * Applied to: Cover frames only (first item in readingOrder with cover elements)
             *
             * @see FUL-159 - Portada de EPUB no accesible en algunas publicaciones
             * @see https://github.com/edrlab/r2-navigator-js/issues/18
             * @see https://github.com/readium/readium-css/issues/55
             */
            try {
                if (this._isCoverFrame(_wnd)) {
                    this._applyCoverFrameStyles(_wnd.document);
                }
            } catch(e) {
                console.warn('[R2] Error applying cover frame styles:', e.message);
            }

            _wnd.focus();
            this.onLoadFrame(_wnd);
            if (!this.isFixedLayout()) {
                this._pagebreakTracker?.observeFrame(_wnd);
            }
        },
        positionChanged: (_locator) => {
            this.onChangePosition(_locator);

            this.handlePositionChange(_locator);

            if (!this.isFixedLayout()) {
                this._pagebreakTracker?.checkVisibility();
            }

            window.focus();
        },
        tap: (_e) => {
            if(this.isHighlightElement(_e.targetElement)) {
                return true;
            }
            this.handleTap(_e);
            return true;
        },
        click: (_e) => {
            if(this.isHighlightElement(_e.targetElement)) {
                return true;
            }
            this.handleClick(_e);
            return true;
        },
        zoom: (_scale) => {
        },
        /**
         * Fires on scroll events within the EPUB content.
         *
         * Uses navigator.isScrollEnd / isScrollStart to determine if
         * we are physically at the boundary of the rendered content,
         * rather than relying on scroll direction (delta).
         *
         * Calls onEndOfChapter / onStartOfChapter directly (bypassing
         * the flag-based handlers) because these are idempotent and
         * must re-evaluate on every scroll — the flag system is designed
         * for paged-mode position changes and would block legitimate
         * scroll-boundary detections.
         */
        scroll: (_delta) => {
            const nav = this.epubNavigator;
            if (!nav) return;
            const loc = nav.currentLocator;
            if (!loc?.locations) return;

            const isNotLastPage = loc.locations.position !== nav.positions.length;
            const isNotFirstPage = loc.locations.position !== 1;

            if (nav.isScrollEnd && isNotLastPage) {
                this.onEndOfChapter();
            }
            if (nav.isScrollStart && isNotFirstPage) {
                this.onStartOfChapter();
            }
        },
        miscPointer: (_amount) => {
        },
        customEvent: (_key, _data) => {
        },
        handleLocator: (seekLocation) => {
            const href = seekLocation.href;
            
            if (href.startsWith("http://") ||
                href.startsWith("https://") ||
                href.startsWith("mailto:") ||
                href.startsWith("tel:")) {
                if (confirm(`Open "${href}" ?`)) {
                    window.open(href, "_blank");
                    return true;
                }
            } 
            
            const locator = this.epubNavigator.positions[seekLocation.locations.position] ?? null;
            
            if (locator) {
                this.goToLocator(locator);
                return true;
            }
            
            console.warn("Unhandled locator", seekLocation);
            return false;
        },
        // @R2 Backup selection handler: R2's native textSelected event fires when the
        // user finishes selecting text inside an iframe. This is a secondary path —
        // the primary path is _setupSelectionListeners (pointerup on iframe docs) in
        // HighlightManager, which fires more reliably across all iframe configurations.
        // Both paths call onSelectionInProgress to show the highlight popup.
        textSelected: (_selection) => {
            this.handleTextSelection(_selection);
        }
    }

    /**
     * Initialize the EPUB viewer with publication data
     *
     * @async
     * @param {Object} config - Initialization configuration
     * @param {string} config.containerId - CSS selector for the container element
     * @param {string} config.epubUrl - URL or identifier of the EPUB to load
     * @returns {Promise<EpubNavigator>} Promise that resolves to the initialized navigator
     */
    async init({containerId, epubUrl}) {
        return new Promise(async (resolve, reject) => {
            // ─── IDPF: revoke blob URLs from any previous init() ───────
            // Guards against leaks if init() runs more than once on the
            // same instance (hot-reload, re-open without unmount, retry).
            // Removed alongside the rest of the IDPF block.
            if (this.idpfFontBlobUrls) {
                for (const url of this.idpfFontBlobUrls.values()) {
                    URL.revokeObjectURL(url);
                }
                this.idpfFontBlobUrls = null;
            }
            // ───────────────────────────────────────────────────────────

            const currentURL = new URL(window.location.href);
            let book = epubUrl;
            let publicationURL = "";
            if(currentURL.searchParams.has("book")) {
                book = currentURL.searchParams.get("book");
            }
            const hostOnly = (u) => { try { return new URL(u).host; } catch { return null; } };
            sendBreadcrumb('init', 'init_start', { epubUrl: hostOnly(book) });
            if(book.startsWith("http://") || book.startsWith("https://") || book.startsWith("file://")) {
                publicationURL = book;
                if(!book.endsWith("manifest.json") && !book.endsWith("/")) publicationURL += "/";
            } else
                publicationURL = `${currentURL.origin}/${book}/`;
            sendBreadcrumb('init', 'publication_url_resolved', { epubUrl: hostOnly(publicationURL) });

            const container = document.body.querySelector(containerId);
            this.container = container;
            const manifestLink = new Link({ href: "manifest.json" });

            // Create the decrypting fetcher
            const fetcher = new DecryptingHttpFetcher(publicationURL);

            const fetched = fetcher.get(manifestLink);
            const selfLink = (await fetched.link()).toURL(publicationURL);

            let currentPhase = 'manifest_fetch';
            try {
                sendBreadcrumb('init', 'manifest_fetch_start');
                const response = await fetched.readAsJSON();

                currentPhase = 'manifest_parse';
                const manifest = Manifest.deserialize(response);
                manifest.setSelfLink(selfLink);
                this.publication = new Publication({ manifest: manifest, fetcher: fetcher });
                sendBreadcrumb('init', 'manifest_parsed', {
                    readingOrderCount: manifest.readingOrder?.items?.length ?? 0,
                    hasToc: !!(manifest.tableOfContents && manifest.tableOfContents.items?.length),
                });

                currentPhase = 'idref_map';
                // @R2 Build idref→href lookup for matching R1 chapters in R2 reader
                this.idrefToHrefMap = await this.buildIdrefToHrefMap();
                this.highlightManager.idrefToHrefMap = this.idrefToHrefMap;
                sendBreadcrumb('init', 'idref_map_built', { size: this.idrefToHrefMap?.size ?? 0 });

                currentPhase = 'last_position';
                let lastPosition;
                let lastPositionSource;
                // If the last position has a locator key, is the new R2 format
                if (this.lastPosition?.locator) {
                    lastPosition = this.lastPosition.locator;
                    lastPositionSource = 'r2';
                } else {
                    // If the last position has a chapterHref key, is the legacy R1 format
                    if (this.lastPosition?.chapterHref) {
                        lastPosition = await this.parseLastPosition(this.lastPosition);
                        lastPositionSource = 'r1_legacy';
                    }
                }

                // Fallback: create a default locator pointing to the first readingOrder item
                // when no saved position exists (e.g. first time opening the book).
                if (!lastPosition) {
                    const firstLink = manifest.readingOrder.items[0];
                    if (firstLink) {
                        lastPosition = new Locator({
                            href: firstLink.href,
                            type: firstLink.type || 'application/xhtml+xml',
                            locations: new LocatorLocations({
                                fragments: [],
                                progression: 0,
                                position: 1,
                            })
                        });
                        lastPositionSource = 'default_first';
                    }
                }
                sendBreadcrumb('init', 'last_position_resolved', {
                    source: lastPositionSource,
                    chapterHref: lastPosition?.href,
                    progression: lastPosition?.locations?.progression,
                });

                // R2 "defaults" control the reading-system layout constraints
                // (as opposed to "preferences" which are user-adjustable settings).
                // - optimalLineLength: sets --RS__defaultLineLength → body max-width
                //   inside iframes. R2 also uses this value to calculate the inline
                //   width it applies to the #readiumViewport container.
                // - minimalLineLength / maximalLineLength: lower and upper bounds
                //   that R2 enforces when computing viewport and column widths.
                // These values are in "em" units relative to the base font size.

                // Generate accessible fonts CSS with absolute URLs for iframe context
                const accessibleFontsCss = getAccessibleFontsCss(window.location.origin);

                currentPhase = 'navigator_construct';
                const nav = new EpubNavigator(
                    container,
                    this.publication,
                    this.listeners,
                    undefined,
                    lastPosition, {
                        preferences: this.settings,
                        defaults: {
                            minimalLineLength: 30,
                            optimalLineLength: 55,
                            maximalLineLength: 75
                        },
                        injectables: {
                            rules: [{
                                resources: [/.*/],
                                append: [
                                    {
                                        as: 'link',
                                        rel: 'stylesheet',
                                        blob: new Blob([SCROLL_MODE_MARGINS_CSS], { type: 'text/css' })
                                    },
                                    {
                                        as: 'link',
                                        rel: 'stylesheet',
                                        blob: new Blob([accessibleFontsCss], { type: 'text/css' })
                                    }
                                ]
                            }],
                            // Allow fonts to load from Volpe's origin (required for CSP font-src)
                            allowedDomains: [window.location.origin]
                        }
                });

                // Guard: ResizeObserver registered in the constructor can fire via
                // requestAnimationFrame before load() finishes creating framePool.
                // Patch resizeHandler to skip if framePool is not yet initialized,
                // or if resize handling is paused (during sidebar animations for Fixed Layout).
                const _origResize = nav.resizeHandler.bind(nav);
                const self = this;
                nav.resizeHandler = async function() {
                    if (!nav.framePool) {
                        sendBreadcrumb('frame', 'resize_skipped_no_framepool', {}, 'warning');
                        return;
                    }
                    if (self._resizePaused) return;
                    return _origResize();
                };

                this.epubNavigator = nav;
                sendBreadcrumb('init', 'navigator_constructed', { layout: nav.layout });

                currentPhase = 'css_prefetch';
                sendBreadcrumb('init', 'css_prefetch_start');
                // Pre-fetch and decrypt all CSS files from the publication,
                // then share the cache with the fetcher so that when nav.load()
                // calls readAsString() for HTML files, the CSS can be inlined
                // directly into the HTML before the navigator's DOMParser processes it.
                //
                // ─── IDPF Font Deobfuscation: kicked off in parallel ───────
                // Runs encryption.xml round-trip concurrently with the CSS
                // prefetch. In books without encryption.xml the 404 stays
                // hidden behind the CSS latency; in books with obfuscated
                // fonts the rewrite happens once the CSS cache is populated.
                //
                // We pass the publication's unique identifier from the
                // already-loaded webpub-manifest (`metadata.identifier`) so
                // the deobfuscator can derive the IDPF SHA-1 key without
                // fetching container.xml + the OPF. The function falls back
                // to the OPF path automatically if the manifest doesn't
                // expose one (custom or older streamers).
                //
                // To remove the IDPF feature: drop this block and the
                // helpers above the class (and the destroy() cleanup).
                const cssCacheReady = this.preFetchCssFiles().then(() => {
                    fetcher.cssCache = this.decryptedCssCache;
                    return this.decryptedCssCache;
                });
                const idpfFontsTask = setupIdpfFontDeobfuscation(
                    fetcher,
                    cssCacheReady,
                    manifest.metadata?.identifier ?? null,
                );

                await cssCacheReady;
                sendBreadcrumb('init', 'css_prefetch_done', { cachedCount: this.decryptedCssCache?.size ?? 0 });
                this.idpfFontBlobUrls = await idpfFontsTask;
                // Expose to the fetcher so DecryptingResource can rewrite
                // inline-style font URLs in HTML/XHTML on the fly.
                fetcher.idpfFontBlobUrls = this.idpfFontBlobUrls;
                // ───────────────────────────────────────────────────────────

                // Hide container during load to prevent layout flash.
                // Readium's FramePool constructor uses spread=true (double layout) by default.
                // By hiding the container, we prevent the user from seeing the initial
                // double layout before we apply the correct layout setting.
                const isFixedLayout = nav.layout === 'fixed';
                if (isFixedLayout) {
                    container.style.visibility = 'hidden';
                }

                // Defense in depth: intercepts "Offscreen load timeout:" unhandled
                // rejections at window level for the entire EpubViewer lifecycle.
                //
                // Originally scoped to the gap between nav.load() and
                // _setupOffscreenTimeoutHandler (where delayedShow doesn't exist
                // yet). Intentionally kept active beyond that window as a safety
                // net: _setupOffscreenTimeoutHandler patches delayedShow.set() to
                // catch timeouts per-promise, but this global listener covers any
                // edge case where a rejection escapes (e.g. promises created
                // before the patch or during teardown). Cleaned up in destroy().
                this._offscreenTimeoutHandler = (event) => {
                    if (isOffscreenTimeoutRejection(event.reason)) {
                        sendBreadcrumb('frame', 'offscreen_timeout_silenced_global', {
                            reason: typeof event.reason === 'string' ? event.reason : null,
                        }, 'warning');
                        event.preventDefault();
                    }
                };
                window.addEventListener('unhandledrejection', this._offscreenTimeoutHandler);

                currentPhase = 'nav_load';
                sendBreadcrumb('init', 'nav_load_start', { layout: nav.layout, isFixedLayout });
                await nav.load();
                sendBreadcrumb('init', 'nav_load_done', { layout: nav.layout, framePoolReady: !!nav.framePool });

                // Apply correct layout immediately after load for Fixed Layout.
                // The framePool now exists, so we can call setPerPage directly before
                // making the content visible.
                if (isFixedLayout && nav.framePool) {
                    currentPhase = 'fxl_apply';
                    const targetColumnCount = this.settings.columnCount;
                    nav.framePool.setPerPage(targetColumnCount);
                    // Force synchronous style update
                    nav.framePool.updateSpineStyle(false);
                    // Restore visibility
                    container.style.visibility = '';
                    sendBreadcrumb('init', 'fxl_layout_applied', { columnCount: targetColumnCount });
                }

                this._setupFrameCommsProtection(nav);
                this._setupAnimationReplacement(nav);
                this._setupOffscreenTimeoutHandler(nav);
                this._setupFxlLoadingState(nav);

                sendBreadcrumb('init', 'init_complete', {
                    layout: nav.layout,
                    chapterHref: lastPosition?.href,
                });
                resolve(nav);
            } catch (error) {
                sendBreadcrumb('init', 'init_failed', {
                    phase: currentPhase,
                    errorName: error?.name,
                    errorMessage: error?.message,
                }, 'error');
                console.error("Error loading manifest", error);
                reject(error);
            }
        });
    }

    /**
     * Re-attach the navigator event listener to each FrameComms after
     * submitPreferences() finishes.
     *
     * Readium's commitCSS() calls framePool.setCSSProperties() which invokes
     * halt() on every FrameComms. halt() clears the listener by design — but
     * Readium also keeps a listenerBuffer that captures messages while no
     * listener is attached. Re-assigning the listener here drains that buffer
     * and restores propagation of selection / scroll / tap events.
     *
     * No internal Readium state is monkey-patched. We rely on the public
     * `framePool.currentFrames` array and the `listener` setter on FrameComms.
     */
    _setupFrameCommsProtection(nav) {
        if (!nav?.framePool || typeof nav.submitPreferences !== 'function') return;

        const originalSubmitPreferences = nav.submitPreferences.bind(nav);
        nav.submitPreferences = async (...args) => {
            const result = await originalSubmitPreferences(...args);
            nav.framePool?.currentFrames?.forEach((frame) => {
                const comms = frame?.msg;
                if (comms) {
                    comms.listener = (key, value) => nav.eventListener(key, value);
                }
            });
            sendBreadcrumb('frame', 'comms_listeners_reattached', {
                frameCount: nav.framePool?.currentFrames?.length ?? 0,
            });
            return result;
        };
    }

    /**
     * Replace CSS transition animation with hide/show for Fixed Layout EPUBs.
     *
     * Readium's updateSpineStyle method uses CSS transitions (150-500ms) when changing
     * layout or resizing, causing a visible "sliding" animation. This patch replaces
     * that animation with a brief hide/show to prevent the visual distraction.
     *
     * @param {Object} nav - The EpubNavigator instance
     */
    _setupAnimationReplacement(nav) {
        // Only apply to Fixed Layout EPUBs
        if (!this.isFixedLayout() || !nav.framePool) return;

        const pool = nav.framePool;

        // Check if spineElement exists (FXLFramePoolManager)
        if (!pool.spineElement) return;

        const originalUpdateSpineStyle = pool.updateSpineStyle.bind(pool);
        const viewer = this; // Reference to EpubViewer instance for flag access

        pool.updateSpineStyle = function (animate, fast = true) {
            // Only suppress animation when flag is set (layout change or sidebar reflow)
            if (animate && viewer._suppressAnimation) {
                // Apply changes without animation
                originalUpdateSpineStyle(false, fast);
            } else {
                // Normal behavior: use original animation or no animation as requested
                originalUpdateSpineStyle(animate, fast);
            }
        };
    }

    /**
     * Patches FXLFramePoolManager's delayedShow Map to handle offscreen load
     * timeout rejections gracefully.
     *
     * PATCH: Readium's FXLFramePoolManager uses a 15-second watchdog timer for
     * offscreen iframe pre-loads. On slow connections, sub-resources (fonts, images)
     * may take longer than 15s to load, causing the watchdog to reject the promise.
     * Since nothing catches this rejection, it surfaces as an UnhandledRejection
     * in Sentry (VOLPE-19H). The timeout serves no functional purpose — the page
     * either loads eventually or gets disposed by the pool.
     *
     * This patch wraps promises stored in delayedShow to convert timeout rejections
     * into silent resolutions, preventing the unhandled rejection without hiding
     * real errors.
     *
     * Additionally fixes a secondary Readium bug: if a timed-out page is later
     * navigated to, waitForItem() would await the rejected promise and throw.
     * The wrapped promise resolves instead, allowing navigation to proceed.
     *
     * Upstream bugs (Readium): watchdog timer is not cancellable, reject uses
     * string instead of Error, rejected promise persists in Map (neutralized
     * by this patch — the wrapped promise resolves undefined instead).
     *
     * @param {Object} nav - The EpubNavigator instance
     */
    _setupOffscreenTimeoutHandler(nav) {
        if (!this.isFixedLayout() || !nav.framePool) {
            sendBreadcrumb('frame', 'offscreen_handler_skipped', {
                reason: !this.isFixedLayout() ? 'not_fxl' : 'no_framepool',
                layout: nav?.layout,
                framePoolReady: !!nav?.framePool,
            }, 'warning');
            return;
        }

        const pool = nav.framePool;
        if (!pool.delayedShow) {
            sendBreadcrumb('frame', 'offscreen_handler_skipped', {
                reason: 'no_delayed_show',
                layout: nav.layout,
                framePoolReady: true,
            }, 'warning');
            return;
        }

        const wrapTimeoutPromise = (promise, frameSrc) => {
            return promise.catch((reason) => {
                if (isOffscreenTimeoutRejection(reason)) {
                    sendBreadcrumb('frame', 'offscreen_timeout_silenced_local', { frameSrc }, 'warning');
                    return;
                }
                throw reason;
            });
        };

        // Wrap promises created during nav.load() so waitForItem() won't throw on stale rejections
        let wrappedCount = 0;
        pool.delayedShow.forEach((existingPromise, key) => {
            pool.delayedShow.set(key, wrapTimeoutPromise(existingPromise, key));
            wrappedCount++;
        });

        // Detect whether `set` was already a non-prototype own property — would indicate
        // someone else patched it before us and we may be wrapping a wrapper.
        const setAlreadyPatched = Object.prototype.hasOwnProperty.call(pool.delayedShow, 'set');

        // Patch future calls
        const originalSet = pool.delayedShow.set.bind(pool.delayedShow);
        pool.delayedShow.set = function(key, promise) {
            sendBreadcrumb('frame', 'offscreen_set_patched', { frameSrc: key });
            return originalSet(key, wrapTimeoutPromise(promise, key));
        };

        sendBreadcrumb('frame', 'offscreen_handler_applied', {
            wrappedCount,
            setAlreadyPatched,
        });
    }

    /**
     * Wraps FXLFramePoolManager.update() to manage loading state during navigation.
     *
     * PATCH: In Readium 2 FXL, there is no loading indicator during page
     * navigation. The user sees a blank screen while framePool.update()
     * builds blobs and loads iframes.
     *
     * Shows a loading indicator only when the current frame is blank (not
     * pre-loaded). Uses a MutationObserver on the iframe's class attribute
     * to detect the exact moment Readium adds the "loaded" class (when
     * content becomes visible). Falls back to clearing loading when update()
     * resolves as a safety net.
     *
     * @param {Object} nav - The EpubNavigator instance
     */
    _setupFxlLoadingState(nav) {
        if (!this.isFixedLayout() || !nav.framePool) return;

        const pool = nav.framePool;
        const originalUpdate = pool.update.bind(pool);
        const viewer = this;
        let pendingUpdates = 0;
        let loadingActive = false;
        let activeObserver = null;

        const stopObserving = () => {
            if (activeObserver) {
                activeObserver.disconnect();
                activeObserver = null;
            }
        };

        const hideLoading = () => {
            if (loadingActive) {
                loadingActive = false;
                stopObserving();
                viewer.onNavigationEnd();
            }
        };

        this._isFxlLoading = () => loadingActive;

        pool.update = async function(...args) {
            pendingUpdates++;
            stopObserving();

            const updatePromise = originalUpdate(...args);

            const currentFrames = pool.currentFrames.filter(Boolean);
            const isScreenBlank = currentFrames.some(fm => !fm.iframe.classList.contains('loaded'));

            if (isScreenBlank) {
                if (!loadingActive) {
                    loadingActive = true;
                    viewer.onNavigationStart();
                }

                activeObserver = new MutationObserver(() => {
                    const allLoaded = currentFrames.every(fm => fm.iframe.classList.contains('loaded'));
                    if (allLoaded) {
                        hideLoading();
                    }
                });

                currentFrames.forEach(fm => {
                    activeObserver.observe(fm.iframe, {
                        attributes: true,
                        attributeFilter: ['class'],
                    });
                });
            }

            try {
                await updatePromise;
            } finally {
                pendingUpdates--;
                if (pendingUpdates === 0) {
                    stopObserving();
                    hideLoading();
                }
            }
        };

        const originalGoForward = nav.goForward.bind(nav);
        nav.goForward = (animated, cb) => {
            if (loadingActive) return cb(false);
            originalGoForward(animated, cb);
        };

        const originalGoBackward = nav.goBackward.bind(nav);
        nav.goBackward = (animated, cb) => {
            if (loadingActive) return cb(false);
            originalGoBackward(animated, cb);
        };

        const originalGo = nav.go.bind(nav);
        nav.go = (locator, animated, cb) => {
            if (loadingActive) return cb(false);
            originalGo(locator, animated, cb);
        };

        const originalGoLink = nav.goLink.bind(nav);
        nav.goLink = (link, animated, cb) => {
            if (loadingActive) return cb(false);
            originalGoLink(link, animated, cb);
        };
    }

    /**
     * Get the chapter HREF from content.opf file using robust XML parsing
     * Retrieves the OPF file path from META-INF/container.xml.
     * EPUB standard requires container.xml to define the rootfile location.
     *
     * @returns {Promise<{opfPath: string, opfDir: string}|null>} Object with opfPath (full path to OPF)
     *          and opfDir (directory containing OPF for resolving relative hrefs), or null on failure
     */
    async getOpfPathFromContainer() {
        try {
            const containerLink = new Link({ href: "META-INF/container.xml" });
            const container = await this.publication.fetcher.get(containerLink);
            const containerText = await container.readAsString();

            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(containerText, "application/xml");

            const parserError = xmlDoc.querySelector("parsererror");
            if (parserError) {
                console.error("XML parsing error in container.xml:", parserError.textContent);
                return null;
            }

            const rootfiles = xmlDoc.getElementsByTagNameNS('*', 'rootfile');
            for (const rootfile of rootfiles) {
                const mediaType = rootfile.getAttribute('media-type');
                if (mediaType === 'application/oebps-package+xml') {
                    const opfPath = rootfile.getAttribute('full-path');
                    if (opfPath) {
                        const lastSlash = opfPath.lastIndexOf('/');
                        const opfDir = lastSlash >= 0 ? opfPath.substring(0, lastSlash + 1) : '';
                        return { opfPath, opfDir };
                    }
                }
            }

            console.warn("No OPF rootfile found in container.xml");
            return null;
        } catch (error) {
            console.error("Error reading container.xml:", error);
            return null;
        }
    }

    /**
     * Get the chapter HREF from OPF file using robust XML parsing.
     * Reads META-INF/container.xml to find the OPF location.
     */
    async getContentOpf(legacyLastPosition) {
        if (!legacyLastPosition || !legacyLastPosition.chapterHref) {
            return null;
        }

        try {
            const opfInfo = await this.getOpfPathFromContainer();
            if (!opfInfo) {
                console.warn("Could not determine OPF path from container.xml");
                return null;
            }

            const { opfPath, opfDir } = opfInfo;
            const contentOpfLink = new Link({ href: opfPath });
            const contentOpf = await this.publication.fetcher.get(contentOpfLink);
            const contentOpfText = await contentOpf.readAsString();

            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(contentOpfText, "application/xml");

            const parserError = xmlDoc.querySelector("parsererror");
            if (parserError) {
                console.error("XML parsing error in OPF:", parserError.textContent);
                return null;
            }

            const target = legacyLastPosition.chapterHref;

            let manifestItem = null;
            const items = xmlDoc.getElementsByTagNameNS('*', 'item');
            for (const item of items) {
                const itemId = item.getAttribute('id');
                const itemHref = item.getAttribute('href');
                if (itemId === target || itemHref === target) {
                    manifestItem = item;
                    break;
                }
            }

            if (!manifestItem) {
                const normalizedTarget = target.replace(/^(\.\.\/)+([^/]+\/)?/, '').replace(/^[^/]+\//, '');
                for (const item of items) {
                    const itemHref = item.getAttribute('href') || '';
                    if (itemHref.endsWith(normalizedTarget) || normalizedTarget.endsWith(itemHref.split('/').pop())) {
                        manifestItem = item;
                        break;
                    }
                }
            }

            if (!manifestItem) {
                console.warn(`Chapter "${target}" not found in OPF manifest`);
                return null;
            }

            const href = manifestItem.getAttribute("href");
            if (!href) {
                console.warn(`No href attribute found for chapter "${target}"`);
                return null;
            }

            const normalizedHref = href.startsWith(opfDir) ? href : `${opfDir}${href}`;

            return normalizedHref;

        } catch (error) {
            console.error("Error parsing OPF file:", error);
            return null;
        }
    }

    /**
     * @R2 Build a lookup map from OPF manifest item id (idref) to href.
     *
     * R1 stored epubChapter as the spine item's idref (e.g. "book2"),
     * while R2 uses the actual file href (e.g. "OEBPS/GeographyofBli_body_split_002.html").
     * This map bridges the gap so legacy highlights can be matched to the
     * correct chapter in the R2 reader.
     *
     * @returns {Map<string, string>} idref → href (raw href from OPF manifest)
     */
    async buildIdrefToHrefMap() {
        const map = new Map();

        try {
            const opfInfo = await this.getOpfPathFromContainer();
            if (!opfInfo) {
                console.warn('@R2 buildIdrefToHrefMap: could not determine OPF path from container.xml');
                return map;
            }

            const { opfPath, opfDir } = opfInfo;
            const contentOpfLink = new Link({ href: opfPath });
            const contentOpf = await this.publication.fetcher.get(contentOpfLink);
            const contentOpfText = await contentOpf.readAsString();

            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(contentOpfText, "application/xml");

            if (xmlDoc.querySelector("parsererror")) {
                console.warn('@R2 buildIdrefToHrefMap: OPF parse error');
                return map;
            }

            const items = xmlDoc.getElementsByTagNameNS('*', 'item');
            for (const item of items) {
                const itemId = item.getAttribute('id');
                const itemHref = item.getAttribute('href');
                if (itemId && itemHref) {
                    const fullHref = itemHref.startsWith(opfDir) ? itemHref : `${opfDir}${itemHref}`;
                    map.set(itemId, fullHref);
                }
            }

            console.log(`@R2 buildIdrefToHrefMap: built map with ${map.size} entries`);
        } catch (error) {
            console.warn('@R2 buildIdrefToHrefMap: could not fetch OPF:', error.message);
        }

        return map;
    }

    async parseLastPosition(legacyLastPosition) {
        // First try to resolve the legacy chapterHref directly from the
        // readingOrder — this avoids fetching content.opf which the Go
        // streamer does not serve as a standalone resource.
        let chapterHref = this._resolveChapterFromReadingOrder(legacyLastPosition);

        // Fallback: try content.opf if readingOrder lookup failed
        if (!chapterHref) {
            chapterHref = await this.getContentOpf(legacyLastPosition);
        }

        let lastReadingPosition;
        if(chapterHref) {
            let progression = 0;
            if (legacyLastPosition.cfi) {
                const lastReadingPositionCFI = parseCFI(legacyLastPosition.cfi);
                progression = parseInt(lastReadingPositionCFI.elementPath[1]) / 100 || 0;
            }

            // @R2 Use manifest positions (not readingOrder index) because
            // FramePoolManager.update() trusts position over href.
            let position = 1;
            try {
                const r2Positions = await this.publication.positionsFromManifest();
                if (r2Positions?.length) {
                    const firstMatchingPosition = r2Positions.find(p => p.href === chapterHref);
                    if (firstMatchingPosition?.locations?.position) {
                        position = firstMatchingPosition.locations.position;
                    }
                }
            } catch (e) {
                console.warn('[R2] positionsFromManifest failed, using readingOrder fallback', e);
                if (this.publication?.readingOrder?.items && chapterHref) {
                    const foundIndex = this.publication.readingOrder.items.findIndex(item => item.href === chapterHref);
                    position = foundIndex >= 0 ? foundIndex + 1 : 1;
                }
            }

            lastReadingPosition = {
                "href": chapterHref,
                "locations": {
                    "position": position,
                    "progression": progression,
                },
                "type": "application/xhtml+xml",
                "title": ""
            };
        }

        return lastReadingPosition;
    }

    /**
     * Resolve a legacy R1 chapterHref directly from the publication's
     * readingOrder without needing to fetch content.opf.
     *
     * The legacy chapterHref can be:
     * - A full OEBPS-relative path: "OEBPS/page004.xhtml"
     * - A bare filename: "page004.xhtml"
     * - An OPF item id: "book4" (cannot be resolved here)
     */
    _resolveChapterFromReadingOrder(legacyLastPosition) {
        if (!legacyLastPosition?.chapterHref || !this.publication?.readingOrder?.items) {
            return null;
        }

        const target = legacyLastPosition.chapterHref;
        const items = this.publication.readingOrder.items;

        // Direct match
        const directMatch = items.find(item => item.href === target);
        if (directMatch) return directMatch.href;

        // Suffix match (e.g. "page004.xhtml" matches "OEBPS/page004.xhtml")
        const suffixMatch = items.find(item =>
            item.href.endsWith('/' + target) || target.endsWith('/' + item.href)
        );
        if (suffixMatch) return suffixMatch.href;

        // Filename-only match
        const targetFilename = target.split('/').pop();
        const filenameMatch = items.find(item =>
            item.href.split('/').pop() === targetFilename
        );
        if (filenameMatch) return filenameMatch.href;

        return null;
    }

    /**
     * Extract the publication-relative directory from the Readium <base> href.
     * e.g. "https://host/token/OEBPS/cover.xhtml" with pub base "https://host/token/"
     *      → "OEBPS/"
     * Returns null if the base href can't be resolved.
     */
    _dirFromBaseHref(baseHref) {
        if (!baseHref) return null;
        try {
            const pubBase = this.publication?.baseURL;
            if (!pubBase) return null;
            // Strip the publication base to get the relative item path
            let relative = baseHref;
            if (relative.startsWith(pubBase)) {
                relative = relative.substring(pubBase.length);
            }
            // Get directory part
            const lastSlash = relative.lastIndexOf('/');
            return lastSlash >= 0 ? relative.substring(0, lastSlash + 1) : '';
        } catch {
            return null;
        }
    }

    /**
     * Extract the publication-relative href from a full URL.
     * e.g. "https://host/token/OEBPS/toc.xhtml" with pub base "https://host/token/"
     *      → "OEBPS/toc.xhtml"
     * @param {string} fullHref - Full URL from <base data-readium>
     * @returns {string} Relative href suitable for R2 Locator navigation
     */
    _extractRelativeHref(fullHref) {
        if (!fullHref) return '';
        try {
            const pubBase = this.publication?.baseURL;
            if (!pubBase) return fullHref;
            if (fullHref.startsWith(pubBase)) {
                return fullHref.substring(pubBase.length);
            }
            return fullHref;
        } catch {
            return fullHref;
        }
    }

    /**
     * Determines if the current frame is a cover document.
     * Checks multiple indicators: first item in readingOrder, 'cover' rel,
     * or DOM elements with cover-related classes/attributes.
     * @param {Window} _wnd - The iframe window object
     * @returns {boolean} True if the frame appears to be a cover
     */
    _isCoverFrame(_wnd) {
        try {
            const currentHref = this.epubNavigator?.currentLocation?.href;
            if (!currentHref) return false;

            const readingOrder = this.publication?.readingOrder?.items;
            if (!readingOrder?.length) return false;

            const firstItem = readingOrder[0];

            // Check 1: Is this the first item in readingOrder?
            const isFirstItem = currentHref.endsWith(firstItem.href) ||
                                firstItem.href.endsWith(currentHref.split('/').pop());

            // Check 2: Does the first item have 'cover' in its rels?
            const hasCoverRel = firstItem.rels?.has?.('cover') ||
                                (Array.isArray(firstItem.rels) && firstItem.rels.includes('cover'));

            // Check 3: Does the DOM contain cover-related elements?
            const doc = _wnd.document;
            const hasCoverElements = !!(
                doc.querySelector('section.cover') ||
                doc.querySelector('.cover') ||
                doc.querySelector('.capa') ||
                doc.querySelector('[epub\\:type="cover"]') ||
                doc.querySelector('img.coverimage') ||
                doc.querySelector('img[role="doc-cover"]') ||
                doc.querySelector('svg > image') // SVG-wrapped cover images
            );

            // Check 4: Is the body class 'fullpage' with cover section (common EPUB pattern)?
            const hasFullpageCover = doc.body?.classList?.contains('fullpage') &&
                                     (doc.querySelector('section.cover') || doc.querySelector('.capa'));

            return (isFirstItem && (hasCoverRel || hasCoverElements)) ||
                   hasFullpageCover ||
                   (isFirstItem && hasCoverElements);
        } catch {
            return false;
        }
    }

    /**
     * Applies CSS fixes to cover frames to prevent image clipping.
     * Injects COVER_FRAME_CSS and sets inline styles on key elements.
     * This fixes the ReadiumCSS pageGutter margin issue that causes
     * cover images to overflow the viewport.
     *
     * Inline styles are applied to html, body, cover container, and cover image
     * to ensure maximum specificity over ReadiumCSS rules in both paged and
     * scroll modes.
     *
     * @param {Document} doc - The iframe document
     */
    _applyCoverFrameStyles(doc) {
        if (!doc?.head) return;

        // In scroll mode, reduce height to leave space for the "Next section" button.
        // In paged mode, use full viewport.
        const isScrollMode = this.settings?.scroll === true;
        const maxHeight = isScrollMode ? 'calc(100vh - 200px)' : '100vh';

        // Remove any existing cover fix style to avoid duplicates on re-apply
        const existingStyle = doc.querySelector('style[data-volpe-cover-fix]');
        if (existingStyle) {
            existingStyle.remove();
        }

        // Inject cover-specific CSS with the variable inside the style tag.
        // IMPORTANT: We set --cover-max-height via :root in a <style> tag instead of
        // inline on documentElement.style because Readium's submitPreferences() calls
        // setCSSProperties() which removes any inline CSS properties not in its list.
        const coverStyle = doc.createElement('style');
        coverStyle.setAttribute('type', 'text/css');
        coverStyle.setAttribute('data-volpe-cover-fix', 'true');
        coverStyle.textContent = `:root { --cover-max-height: ${maxHeight}; }\n${COVER_FRAME_CSS}`;
        doc.head.appendChild(coverStyle);
    }

    parseSettings(settings) {
        const themeColors = this.getThemePreferences(settings.theme);

        let r2Settings = {
            backgroundColor: themeColors.backgroundColor,
            blendFilter: null,
            columnCount: settings.layout == null || settings.layout === 'single' ? 1 : 2,
            constraint: null,
            darkenFilter: null,
            deprecatedFontSize: null,
            fontFamily: settings.fontFamily || null,
            fontSize: this.currentFontSize !== FONT_SIZE_DEFAULT ? this.currentFontSize : null,
            fontSizeNormalize: null,
            fontOpticalSizing: null,
            fontOverride: null,
            fontWeight: null,
            fontWidth: null,
            hyphens: null,
            invertFilter: null,
            invertGaijiFilter: null,
            iPadOSPatch: null,
            layoutStrategy: null,
            letterSpacing: null,
            ligatures: null,
            lineHeight: settings.lineHeight ?? null,
            linkColor: themeColors.linkColor,
            noRuby: null,
            pageGutter: settings.margins ?? MARGINS_DEFAULT,
            paragraphIndent: null,
            paragraphSpacing: null,
            scroll: settings.layout === 'scroll',
            // ReadiumCSS applies `--RS__pageGutter` as body horizontal padding only
            // when scroll is off (selector: `:root:not([style*="readium-scroll-on"]) body`).
            // Mirror the gutter into scrollPaddingLeft/Right so horizontal margins
            // remain effective in scroll mode.
            scrollPaddingLeft: settings.margins ?? MARGINS_DEFAULT,
            scrollPaddingRight: settings.margins ?? MARGINS_DEFAULT,
            selectionBackgroundColor: themeColors.selectionBackgroundColor,
            selectionTextColor: themeColors.selectionTextColor,
            textAlign: null,
            textColor: themeColors.textColor,
            textNormalization: null,
            visitedColor: null,
            wordSpacing: null,
        };
        return r2Settings;
    }

    preventContextMenu(e) {
        e.preventDefault();
        e.stopPropagation();
    }

    handleKeyboardArrowKeys(e) {
        if (!this.isKeyboardNavigationEnabled() || this._isFxlLoading()) {
            return;
        }

        if (e.keyCode == 37 || e.keyCode == 39) {
            e.preventDefault();
            this.onKeyboardArrowPressed(e);
        }

        if (e.keyCode == 37) {
            if (this.epubNavigator.currentProgression === 'ltr') {
                this.goPrev();
            } else {
                this.goNext();
            }
        }
        if (e.keyCode == 39) {
            if (this.epubNavigator.currentProgression === 'ltr') {
                this.goNext();
            } else {
                this.goPrev();
            }
        }
    }

    /**
     * Get table of contents items from the current publication.
     *
     * @readium/shared 2.x: tableOfContents was renamed to toc.
     * R2 ToC items use `title` and `href`, but ContentList.vue expects `label` and `chapterHref`.
     * This method normalizes the items to the expected format.
     */
    getTocItems() {
        const rawItems = this.epubNavigator.publication?.toc?.items || [];
        return this.normalizeTocItems(rawItems);
    }

    /**
     * Recursively normalize R2 ToC items to the format expected by ContentList.vue.
     * Maps R2 { title, href, children } → { label, chapterHref, items }
     */
    normalizeTocItems(items) {
        if (!items || !Array.isArray(items)) return [];
        return items.map(item => ({
            label: item.title || item.label || '',
            chapterHref: item.href || item.chapterHref || '',
            page: item.page || null,
            items: this.normalizeTocItems(item.children || item.items || []),
        }));
    }

    getCurrentLocation() {
        return this.epubNavigator.currentLocation;
    }

    /**
     * Map Volpe theme names to R2 color preferences.
     *
     * Readium 2 does NOT have a "theme" preference — it uses individual
     * color properties (backgroundColor, textColor, linkColor) that get
     * applied as CSS custom properties inside the EPUB iframes.
     */
    static themePreferences = {
        dark: {
            backgroundColor: '#141414',
            textColor: '#d7d7db',
            linkColor: '#368ace',
            selectionBackgroundColor: '#4a5a7a',
            selectionTextColor: '#ffffff',
        },
        light: {
            backgroundColor: null,
            textColor: null,
            linkColor: null,
            selectionBackgroundColor: null,
            selectionTextColor: null,
        },
        cream: {
            backgroundColor: '#fefce8',
            textColor: '#38342C',
            linkColor: '#946B2D',
        },
        sepia: {
            backgroundColor: '#f4ecd8',
            textColor: '#5C4033',
            linkColor: '#6B4F0F',
        },
        cyan: {
            backgroundColor: '#ecfeff',
            textColor: '#164E63',
            linkColor: '#0B5E73',
        },
        'high-contrast-light': {
            backgroundColor: '#ffffff',
            textColor: '#000000',
            linkColor: '#0000CC',
        },
        'high-contrast-dark': {
            backgroundColor: '#000000',
            textColor: '#FFFFFF',
            linkColor: '#00E5FF',
        },
    };

    getThemePreferences(theme) {
        if (theme === 'system') {
            const isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
            return EpubViewer.themePreferences[isDark ? 'dark' : 'light'];
        }
        return EpubViewer.themePreferences[theme] || EpubViewer.themePreferences.light;
    }

    changeTheme(theme) {
        sendBreadcrumb('layout', 'theme_changed', { theme });
        this.epubNavigator.submitPreferences(this.getThemePreferences(theme));
    }

    /**
     * Changes the font family for EPUB content.
     * @param {string|null} fontFamily - CSS font-family value or null for default
     */
    changeFontFamily(fontFamily) {
        sendBreadcrumb('layout', 'font_family_changed', { fontFamily });
        this.epubNavigator?.submitPreferences({ fontFamily });
    }

    /**
     * Changes the line height for EPUB content.
     * @param {number|null} lineHeight - Line height value (e.g. 1.2, 1.5, 2.0) or null for default
     */
    changeLineHeight(lineHeight) {
        sendBreadcrumb('layout', 'line_height_changed', { lineHeight });
        this.epubNavigator?.submitPreferences({ lineHeight });
    }

    /**
     * Changes the page margins for EPUB content.
     * @param {number} pageGutter - Margin value in pixels (e.g. 5, 20, 60)
     */
    changeMargins(pageGutter) {
        sendBreadcrumb('layout', 'margins_changed', { pageGutter });
        this.epubNavigator?.submitPreferences({
            pageGutter,
            scrollPaddingLeft: pageGutter,
            scrollPaddingRight: pageGutter,
        });
    }

    updateLayout(layout = 'single', onComplete = () => {}) {
        const epubLayout = this.epubLayouts[layout];
        const preferences = {};

        if (layout === 'scroll' && this.isPreviewMode) {
            console.debug('Prevent scroll mode in EPUB previews');
            layout = 'single';
        }

        if (epubLayout.scroll === 'scroll-doc') {
            preferences.scroll = true;
        } else {
            preferences.scroll = false;

            if (layout === 'single') {
                preferences.columnCount = 1;
            } else if (layout === 'double') {
                preferences.columnCount = 2;
            } else {
                preferences.columnCount = null;
            }
        }

        sendBreadcrumb('layout', 'layout_changed', {
            layout,
            columnCount: preferences.columnCount ?? null,
        });

        // Suppress animation during layout change
        this._suppressAnimation = true;

        if (this.isFixedLayout()) {
            this._applyLayoutWithFade(preferences, epubLayout, onComplete);
        } else {
            this.epubNavigator.submitPreferences(preferences);
            setTimeout(() => {
                this._suppressAnimation = false;
            }, 200);
            onComplete(epubLayout);
        }
    }

    /**
     * Applies layout change with fade animation for Fixed Layout EPUBs.
     * Uses ReadiumFadeAnimator for the common fade pattern.
     * @param {Object} preferences - Readium preferences to apply
     * @param {Object} epubLayout - Layout configuration object
     * @param {Function} onComplete - Callback when layout change is complete
     */
    _applyLayoutWithFade(preferences, epubLayout, onComplete) {
        sendBreadcrumb('layout', 'layout_fade_start', { epubLayout: epubLayout?.scroll ?? null });
        const bookElement = this.getBookElement();

        if (!bookElement) {
            this.epubNavigator.submitPreferences(preferences);
            setTimeout(() => {
                this._suppressAnimation = false;
            }, 200);
            onComplete(epubLayout);
            sendBreadcrumb('layout', 'layout_fade_end', { epubLayout: epubLayout?.scroll ?? null });
            return;
        }

        this._fadeAnimator.animate(
            bookElement,
            async () => {
                await this.epubNavigator.submitPreferences(preferences);
            },
            () => {
                this._suppressAnimation = false;
                onComplete(epubLayout);
                sendBreadcrumb('layout', 'layout_fade_end', { epubLayout: epubLayout?.scroll ?? null });
            }
        );
    }

    /**
     * Gets the current font size from internal tracking.
     * @returns {number} Current font size in rem
     */
    getCurrentFontSize() {
        return this.currentFontSize;
    }

    /**
     * Sets the current font size (used for syncing with stored preferences).
     * @param {number} fontSize - Font size in rem
     */
    setCurrentFontSize(fontSize) {
        if (typeof fontSize === 'number' && fontSize >= FONT_SIZE_MIN && fontSize <= FONT_SIZE_MAX) {
            this.currentFontSize = fontSize;
            sendBreadcrumb('layout', 'font_size_changed', { fontSize });
            this.epubNavigator?.submitPreferences({ fontSize }).then(() => {
                this.triggerZoomReflow();
            });
        }
    }

    /**
     * Pauses the ResizeObserver handling during sidebar animations.
     * Used for Fixed Layout EPUBs to prevent intermediate reflows.
     */
    pauseResizeHandling() {
        this._resizePaused = true;
    }

    /**
     * Resumes the ResizeObserver handling after sidebar animations complete.
     */
    resumeResizeHandling() {
        this._resizePaused = false;
    }

    /**
     * Gets the spine element for Fixed Layout EPUBs.
     * @returns {HTMLElement|null} The spine element or null if not available
     */
    getSpineElement() {
        if (this.epubNavigator?.layout !== 'fixed') return null;
        return this.epubNavigator?.pool?.spineElement || null;
    }

    /**
     * Gets the book element for Fixed Layout EPUBs.
     * Unlike spineElement, bookElement is not modified by Readium during layout changes,
     * making it safe for fade animations.
     * @returns {HTMLElement|null} The book element or null if not available
     */
    getBookElement() {
        if (this.epubNavigator?.layout !== 'fixed') return null;
        return this.epubNavigator?.pool?.bookElement || null;
    }

    /**
     * Checks if the current EPUB is Fixed Layout.
     * @returns {boolean}
     */
    isFixedLayout() {
        return this.epubNavigator?.layout === 'fixed';
    }

    /**
     * Triggers a reflow of the EPUB content.
     * Calls resizeHandler to recalculate layout, then navigates to current position.
     * Used by SidebarViewportAnimator after viewport layout changes.
     */
    triggerResize() {
        if (!this.epubNavigator) return;

        const currentLocation = this.getCurrentLocation();
        const isFixedLayout = this.epubNavigator.layout === 'fixed';

        // For Fixed Layout EPUBs, find the leftmost page of the current spread
        let targetLocator = null;
        if (isFixedLayout && this.epubNavigator._cframes?.length > 0) {
            try {
                const positions = this.epubNavigator.positions || [];
                for (const cframe of this.epubNavigator._cframes) {
                    const href = cframe?.window?.frameElement?.getAttribute('data-original-href');
                    if (href) {
                        const locator = positions.find(pos => pos.href === href);
                        if (locator) {
                            const posNum = locator.locations?.position;
                            if (!targetLocator || posNum < targetLocator.locations?.position) {
                                targetLocator = locator;
                            }
                        }
                    }
                }
            } catch (e) { /* ignore */ }
        }

        // Fallback to currentLocation for non-FXL or if no locator found
        if (!targetLocator) {
            targetLocator = currentLocation;
        }

        this._suppressAnimation = true;

        // Call resizeHandler to recalculate layout based on current viewport dimensions
        this.epubNavigator.resizeHandler().then(() => {
            // Wait for next paint to ensure layout is fully calculated before navigating
            requestAnimationFrame(() => {
                if (targetLocator) {
                    this.epubNavigator?.go?.(targetLocator, true, () => {});
                }
                this._suppressAnimation = false;
            });
        });
    }

    /**
     * Triggers a reflow after font size changes.
     * Forces a viewport resize to make Readium recalculate column layout.
     */
    triggerZoomReflow() {
        if (!this.container || !this.epubNavigator) return;

        const currentLocation = this.getCurrentLocation();
        const parentElement = this.container.parentElement;
        if (!parentElement) return;

        const originalPadding = parentElement.style.paddingRight;

        // Add temporary padding to force ResizeObserver to trigger
        parentElement.style.paddingRight = '1px';

        // Wait for ResizeObserver to process
        setTimeout(() => {
            // Restore original padding
            parentElement.style.paddingRight = originalPadding;

            // Wait for second resize, then navigate to position
            setTimeout(() => {
                if (currentLocation) {
                    this.epubNavigator?.go?.(currentLocation, true, () => {});
                }
            }, 100);
        }, 50);
    }

    /**
     * Increases the font size by FONT_SIZE_STEP, up to FONT_SIZE_MAX.
     * @returns {number} The new font size in rem
     */
    zoomIn() {
        if (this.currentFontSize >= FONT_SIZE_MAX) {
            return this.currentFontSize;
        }
        this.currentFontSize = Math.min(FONT_SIZE_MAX, this.currentFontSize + FONT_SIZE_STEP);
        sendBreadcrumb('layout', 'zoom_in', { zoom: this.currentFontSize });
        this.epubNavigator?.submitPreferences({ fontSize: this.currentFontSize }).then(() => {
            this.triggerZoomReflow();
        });
        return this.currentFontSize;
    }

    /**
     * Decreases the font size by FONT_SIZE_STEP, down to FONT_SIZE_MIN.
     * @returns {number} The new font size in rem
     */
    zoomOut() {
        if (this.currentFontSize <= FONT_SIZE_MIN) {
            return this.currentFontSize;
        }
        this.currentFontSize = Math.max(FONT_SIZE_MIN, this.currentFontSize - FONT_SIZE_STEP);
        sendBreadcrumb('layout', 'zoom_out', { zoom: this.currentFontSize });
        this.epubNavigator?.submitPreferences({ fontSize: this.currentFontSize }).then(() => {
            this.triggerZoomReflow();
        });
        return this.currentFontSize;
    }

    /**
     * Resets the font size to FONT_SIZE_DEFAULT (100%).
     * @returns {number} The default font size in rem
     */
    zoomReset() {
        this.currentFontSize = FONT_SIZE_DEFAULT;
        sendBreadcrumb('layout', 'zoom_reset', { zoom: this.currentFontSize });
        this.epubNavigator?.submitPreferences({ fontSize: this.currentFontSize }).then(() => {
            this.triggerZoomReflow();
        });
        return this.currentFontSize;
    }

    handleEndOfChapter(locator, method = 'unknown') {
        if (this.isChapterEnd) {
            return;
        }

        this.isChapterEnd = true;

        sendBreadcrumb('nav', 'end_of_chapter', {
            chapterHref: locator?.href,
            method,
        });

        if(locator.locations.position !== this.epubNavigator.positions.length) {
            this.onEndOfChapter();
        }
    }

    handleStartOfChapter(locator) {
        if (this.isChapterStart) {
            return;
        }

        this.isChapterStart = true;

        sendBreadcrumb('nav', 'start_of_chapter', {
            chapterHref: locator?.href,
        });

        if (locator.locations.position !== 1) {
            this.onStartOfChapter();
        }
    }

    /**
     * Detects chapter boundaries by comparing adjacent positions.
     *
     * This handles paged mode where each page is a discrete position.
     * For scroll mode, the `scroll` listener handles boundary detection
     * via overscroll events — do NOT mix isScrollEnd/isScrollStart here
     * as they can be true simultaneously in short chapters or paged mode,
     * causing both buttons to appear at once.
     */
    checkChapterBoundaries(currentLocation) {
        if (!this.epubNavigator) {
            return;
        }

        try {
            if (currentLocation) {
                if (currentLocation.locations && currentLocation.locations.position !== undefined &&
                currentLocation.locations.totalProgression !== undefined) {

                    const currentPosition = currentLocation.locations.position;
                    const nextPosition = this.epubNavigator.positions[currentPosition];

                    if (currentLocation.href !== nextPosition?.href) {
                        this.handleEndOfChapter(currentLocation, 'position');
                    }

                    const prevPosition = this.epubNavigator.positions[currentPosition - 2];

                    if (currentLocation.href !== prevPosition?.href) {
                        this.handleStartOfChapter(currentLocation, 'position');
                    }
                }
            }
        } catch (error) {
            console.warn('Error checking chapter boundaries:', error);
        }
    }

    handlePositionChange(locator) {
        if (!locator || !locator.href) {
            return;
        }

        let currentChapterHref = locator.href;

        sendBreadcrumb('nav', 'position_changed', {
            chapterHref: currentChapterHref,
            progression: locator.locations?.progression,
            position: locator.locations?.position,
        });

        if (this.currentChapterHref !== currentChapterHref) {
            sendBreadcrumb('nav', 'chapter_changed', {
                from: this.currentChapterHref,
                to: currentChapterHref,
            });
            this.onChapterChange(currentChapterHref, this.currentChapterHref);
            this.currentChapterHref = currentChapterHref;
        }

        this.isChapterEnd = false;
        this.isChapterStart = false;

        this.checkChapterBoundaries(locator);

        // Supplement for scroll mode: when the rendered content fits
        // entirely in the viewport, there is no scrollable area, so the
        // scroll listener never fires. The position-based check above
        // may also miss the boundary because multiple positions can map
        // to the same chapter even when all content is visible.
        //
        // isScrollEnd / isScrollStart reflect the physical scroll state
        // of the iframe. When both are true the content is shorter than
        // the viewport — we treat it as being at both boundaries.
        const nav = this.epubNavigator;
        if (nav) {
            if (nav.isScrollEnd && !this.isChapterEnd) {
                this.handleEndOfChapter(locator, 'scrollEnd');
            }
            if (nav.isScrollStart && !this.isChapterStart) {
                this.handleStartOfChapter(locator);
            }
        }
    }

    /**
     * @R2 Backup selection handler for R2's native textSelected event.
     * Builds selectionData from the browser's DOM selection and fires onSelectionInProgress
     * to show the highlight creation popup.
     *
     * This is the secondary path — the primary one is _setupSelectionListeners (pointerup)
     * in HighlightManager, which works more reliably across all iframe configurations.
     *
     * @param {Object} selection - R2's textSelected event data (text, x, y, width, height, targetFrameSrc)
     */
    handleTextSelection(selection) {
        const targetFrame = this.findFrameBySource(selection.targetFrameSrc);
        if (!targetFrame) return;

        try {
            const frameDocument = targetFrame.contentDocument;
            const frameWindow = targetFrame.contentWindow;
            if (!frameDocument || !frameWindow) return;

            const domSelection = frameWindow.getSelection();
            if (!domSelection || domSelection.rangeCount === 0) return;

            const range = domSelection.getRangeAt(0);
            sendBreadcrumb('selection', 'text_selected', {
                chapterHref: this.highlightManager?.currentChapterHref,
                hasRange: true,
            });

            const selectionData = {
                text: selection.text,
                position: {
                    x: selection.x,
                    y: selection.y,
                    width: selection.width,
                    height: selection.height,
                },
                iframeDocument: frameDocument.defaultView.frameElement,
                range: range,
                startContainer: range.startContainer,
                endContainer: range.endContainer,
                startOffset: range.startOffset,
                endOffset: range.endOffset,
                commonAncestorContainer: range.commonAncestorContainer,
                parentElements: this.getParentElements(range),
            };

            this.onSelectionInProgress(selectionData);
        } catch (error) {
            console.error('Error processing text selection:', error);
        }
    }

    findFrameBySource(targetFrameSrc) {
        const iframes = document.querySelectorAll('#readiumViewport iframe');
        for (const iframe of iframes) {
            if (iframe.src === targetFrameSrc ||
                iframe.contentWindow?.location?.href === targetFrameSrc) {
                return iframe;
            }
        }
        return null;
    }

    rangeOffsetsRelativeToElement(range, element) {
        const startRange = range.cloneRange();
        startRange.selectNodeContents(element);
        startRange.setEnd(range.startContainer, range.startOffset);
        const start = startRange.toString().length;

        const endRange = range.cloneRange();
        endRange.selectNodeContents(element);
        endRange.setEnd(range.endContainer, range.endOffset);
        const end = endRange.toString().length;

        return { start, end };
    }

    getParentElements(range) {
        const startElement = range.startContainer.nodeType === Node.ELEMENT_NODE
            ? range.startContainer
            : range.startContainer.parentElement;

        const endElement = range.endContainer.nodeType === Node.ELEMENT_NODE
            ? range.endContainer
            : range.endContainer.parentElement;

        return {
            startParent: startElement,
            endParent: endElement,
            commonParent: range.commonAncestorContainer.nodeType === Node.ELEMENT_NODE
                ? range.commonAncestorContainer
                : range.commonAncestorContainer.parentElement
        };
    }

    getNodesInRange(range, frameDocument = null) {
        const elements = new Set();
        const doc = frameDocument || document;
        const rootAncestor = range.commonAncestorContainer.nodeType === Node.ELEMENT_NODE
            ? range.commonAncestorContainer
            : range.commonAncestorContainer.parentElement;

        function getElement(node) {
            if (node.nodeType === Node.ELEMENT_NODE) {
                return node;
            }
            if (node.nodeName === 'mark') {
                return getElement(node.parentElement);
            }
            return node.parentElement;
        }

        const startElement = getElement(range.startContainer);
        const endElement = getElement(range.endContainer);

        if (startElement === endElement) {
            elements.add(endElement);
        }

        const walker = doc.createTreeWalker(
            rootAncestor,
            NodeFilter.SHOW_ELEMENT,
            {
                acceptNode: (node) => {
                    if (range.intersectsNode(node)) {
                        return NodeFilter.FILTER_ACCEPT;
                    }
                    return NodeFilter.FILTER_REJECT;
                }
            }
        );

        let node;
        while ((node = walker.nextNode())) {
            elements.add(node);
        }

        return Array.from(elements);
    }

    /**
     * Classifies a Readium FrameClickEvent target as 'highlight' | 'image' | 'text' | 'other'.
     * `event.targetElement` is the outerHTML string of the clicked element, so detection
     * goes through the existing string-based predicates `isHighlightElement` / `isImageElement`.
     * @param {Object} event - The FrameClickEvent
     * @returns {'highlight'|'image'|'text'|'other'}
     */
    _classifyClickTarget(event) {
        if (event?.interactiveElement) return 'other';
        const targetElement = event?.targetElement;
        if (!targetElement) return 'other';
        if (this.isHighlightElement(targetElement)) return 'highlight';
        if (this.isImageElement(targetElement)) return 'image';
        return 'text';
    }

    /**
     * Handles tap events from R2's navigator on touch devices.
     * Implements the full click pipeline: interactive element skip, selection dismiss,
     * image zoom, page turn (quarter-based), and UI toggle.
     * @param {Object} event - The FrameClickEvent from R2's listener system
     */
    handleTap(event) {
        sendBreadcrumb('selection', 'tap', { targetType: this._classifyClickTarget(event) });
        if (event.interactiveElement) {
            return;
        }

        const _cframes = this.epubNavigator._cframes;
        if (_cframes && !this.scrollPageMode && !this._isFxlLoading()) {
            const oneQuarter = ((_cframes.length === 2 ? _cframes[0]?.window.innerWidth + _cframes[1]?.window.innerWidth : _cframes[0]?.window.innerWidth) * window.devicePixelRatio) / 4;
            if (event.x < oneQuarter) {
                this.epubNavigator.goLeft(true, () => {});
                return;
            } else if (event.x > oneQuarter * 3) {
                this.epubNavigator.goRight(true, () => {});
                return;
            }
        }

        if (this.handleImageClick(event)) {
            return;
        }
        this.onToggleUi();
    }

    /**
     * Handles click events from R2's navigator on mouse/pointer devices.
     * Implements the full click pipeline: interactive element skip, selection dismiss,
     * image zoom, and UI toggle.
     * @param {Object} event - The FrameClickEvent from R2's listener system
     */
    handleClick(event) {
        sendBreadcrumb('selection', 'click', { targetType: this._classifyClickTarget(event) });
        if (event.interactiveElement) {
            return;
        }

        if (this.handleImageClick(event)) {
            return;
        }

        this.onToggleUi();
    }

    goNext() {
        const loc = this.getCurrentLocation();
        sendBreadcrumb('nav', 'go_next', {
            chapterHref: loc?.href,
            progression: loc?.locations?.progression,
        });
        this.epubNavigator.goRight(true, (e) => {
            this.onGoNext(e);
        });
    }

    goPrev() {
        const loc = this.getCurrentLocation();
        sendBreadcrumb('nav', 'go_prev', {
            chapterHref: loc?.href,
            progression: loc?.locations?.progression,
        });
        this.epubNavigator.goLeft(true, (e) => {
            this.onGoPrev(e);
        });
    }

    /**
     * Navigate to a specific locator (R2 Locator object)
     * Used by store.js goToLocation action
     *
     * @param {Object} locator - R2 Locator object with href, locations, etc.
     */
    goToLocator(locator) {
        if (!locator) return;
        sendBreadcrumb('nav', 'go_to_locator', {
            chapterHref: locator?.href,
            progression: locator?.locations?.progression,
        });
        this.epubNavigator.go(locator, true, () => {
        });
    }

    /**
     * Navigate to a link (R2 Link object)
     * Used for Table of Contents navigation
     *
     * @param {Object} link - R2 Link object with href
     */
    goToLink(link) {
        if (!link) return;
        const linkObj = new Link({ href: link.href || link });
        sendBreadcrumb('nav', 'go_to_link', { href: linkObj.href });
        this.epubNavigator.goLink(linkObj, true, () => {
        });
    }

    /**
     * Navigate to a legacy R1 location (from notes/highlights created with Readium 1).
     * Resolves the R1 idref to R2 href and uses text for precise navigation.
     *
     * @deprecated This method is scheduled for removal once a reliable bulk migration
     * strategy is implemented. Currently, renderHighlight performs lazy migration of
     * R1 notes to R2 format, but only when a chapter is visited and rendered. This
     * means notes in unvisited chapters remain unmigrated. Until we implement a
     * mechanism to migrate all R1 notes (e.g., background migration on book load or
     * server-side batch migration), this fallback navigation method must remain to
     * ensure users can navigate to unmigrated R1 notes.
     *
     * @param {Object} epubLocation - R1 location object with chapterHref and cfi
     * @param {string} [totalText] - Text of the note/highlight for precise positioning
     */
    goToR1Location(epubLocation, totalText) {
        if (!epubLocation) return;

        const chapterIdref = epubLocation.chapterHref;
        const cfi = epubLocation.cfi;

        // 1. Try to resolve idref to href using the idrefToHrefMap
        let resolvedHref = this.idrefToHrefMap?.get(chapterIdref);

        // 2. Fallback: try to resolve from readingOrder (exact/suffix/filename match)
        if (!resolvedHref) {
            resolvedHref = this._resolveChapterFromReadingOrder({ chapterHref: chapterIdref });
        }

        // 3. Last fallback: use chapterIdref directly (might work if it's already a valid href)
        if (!resolvedHref) {
            resolvedHref = chapterIdref;
        }

        // 4. Extract fragment ID from CFI as fallback (e.g., [introduction_1] from /4/2[introduction_1]/26/1:254)
        let fragmentId = null;
        if (cfi) {
            const fragmentMatch = cfi.match(/\[([^\]]+)\]/);
            fragmentId = fragmentMatch ? fragmentMatch[1] : null;
        }

        // 5. Create Locator with text for precise navigation, fragment as fallback
        const locator = new Locator({
            href: resolvedHref,
            type: 'application/xhtml+xml',
            locations: new LocatorLocations({
                fragments: fragmentId ? [fragmentId] : [],
            }),
            text: totalText
                ? new LocatorText({ highlight: totalText })
                : undefined,
        });

        this.epubNavigator.go(locator, true, (success) => {
            if (!success) {
                console.warn('goToR1Location: navigation failed', { resolvedHref, totalText });
            }
        });
    }

    async preFetchCssFiles() {
        const allResources = this.epubNavigator.publication.resources?.items || [];
        const cssLinks = allResources.filter(item => item.href.endsWith('.css'));

        for (const cssLink of cssLinks) {
            const cssHref = cssLink.href;
            const shouldDecrypt = ContentEncryptionService.shouldDecryptFile(cssHref);

            if (shouldDecrypt) {
                try {
                    const cssResource = this.epubNavigator.publication.fetcher.get(cssLink);
                    const decryptedCss = await cssResource.readAsString();
                    this.decryptedCssCache.set(cssHref, decryptedCss);
                } catch (error) {
                    console.error('Failed to pre-decrypt CSS:', cssHref, error);
                }
            } else {
                // FIXME: Esto esta mal. Si no está encriptado, el css se obtiene solo por el DOM
                // Even if not encrypted, fetch and cache the raw CSS so inlineCssInHtml() can work
                try {
                    const cssResource = this.epubNavigator.publication.fetcher.get(cssLink);
                    const rawCss = await cssResource.readAsString();
                    this.decryptedCssCache.set(cssHref, rawCss);
                } catch (error) {
                    console.warn('Failed to fetch CSS:', cssHref, error);
                }
            }
        }

        // Resolve @import statements inside cached CSS by inlining the
        // imported CSS content from the cache. Without this, the browser
        // would try to fetch the @import URLs from inside the blob iframe,
        // which fails for encrypted resources.
        resolveImportsInCssCache(this.decryptedCssCache);
    }

    isHighlightElement(targetElement) {
        if (!targetElement || typeof targetElement !== 'string') {
            return false;
        }
        return /^<mark\b[^>]*>/i.test(targetElement.trim());
    }

    /**
     * Determines if a target element HTML string represents an image element.
     * Checks for both HTML <img> and SVG <image> tags.
     * @param {string} targetElement - The outerHTML string of the clicked element from R2's FrameClickEvent
     * @returns {boolean} Whether the element is an img or image tag
     */
    isImageElement(targetElement) {
        if (!targetElement || typeof targetElement !== 'string') {
            return false;
        }
        const trimmed = targetElement.trim();
        return /^<img\b/i.test(trimmed) || /^<image\b/i.test(trimmed);
    }

    /**
     * Resolves the full image URL and alt text from an R2 FrameClickEvent.
     * Uses the event's cssSelector to query the actual DOM element inside the iframe,
     * then resolves relative URLs against the document's baseURI.
     * @param {Object} event - The FrameClickEvent from R2's listener system
     * @returns {{ src: string, alt: string } | null} Image data or null if resolution fails
     */
    resolveImageUrlFromEvent(event) {
        try {
            const cframes = this.epubNavigator._cframes;
            if (!cframes || !event.cssSelector) {
                return null;
            }

            let targetIframeWindow = null;
            for (const cframe of cframes) {
                if (cframe?.window?.location?.href === event.targetFrameSrc) {
                    targetIframeWindow = cframe.window;
                    break;
                }
            }
            if (!targetIframeWindow) {
                targetIframeWindow = cframes[0]?.window;
            }
            if (!targetIframeWindow) {
                return null;
            }

            const targetDomElement = targetIframeWindow.document.querySelector(event.cssSelector);
            if (!targetDomElement) {
                return null;
            }

            const originalUrl =
                targetDomElement.currentSrc ||
                targetDomElement.src ||
                (targetDomElement.href && targetDomElement.href.baseVal) ||
                (targetDomElement.getAttribute && (
                    targetDomElement.getAttribute('href') || targetDomElement.getAttribute('xlink:href')
                ));

            if (!originalUrl) {
                return null;
            }

            const baseUri = targetDomElement.ownerDocument?.baseURI || '';
            let resolvedUrl = originalUrl;
            try {
                resolvedUrl = /^(https?:|blob:|data:)/i.test(originalUrl)
                    ? originalUrl
                    : new URL(originalUrl, baseUri).toString();
            } catch (_) {}

            const altText = targetDomElement.alt
                || (targetDomElement.href?.baseVal)
                || '';

            return { src: resolvedUrl, alt: altText };
        } catch (error) {
            console.warn('[R2] Error resolving image URL from click event:', error);
            return null;
        }
    }

    /**
     * Checks if a click/tap event targeted an image element and invokes the image click callback.
     * @param {Object} event - The FrameClickEvent from R2's listener system
     * @returns {boolean} Whether an image click was detected and handled
     */
    handleImageClick(event) {
        if (!this.isImageElement(event.targetElement)) {
            return false;
        }

        const imageData = this.resolveImageUrlFromEvent(event);
        if (!imageData) {
            return false;
        }

        this.onImageClick(imageData);
        return true;
    }

    initHighlights(getSelectionsState, context) {
        // @R2 Pass current content frames so HighlightManager can find the visible body
        this.highlightManager.getContentFrames = () => this.epubNavigator?._cframes || [];
        // @R2 Pass function to extract relative href from full URL for navigation
        this.highlightManager.getRelativeHref = (fullHref) => this._extractRelativeHref(fullHref);
        this.highlightManager.initHighlights(getSelectionsState, context, this.getNodesInRange.bind(this));
    }

    updateCurrentChapterHighlights(selections, context) {
        this.highlightManager.updateCurrentChapterHighlights(selections, context);
    }

    renderHighlights() {
        this.highlightManager.renderHighlights(this.getNodesInRange.bind(this));
    }

    renderHighlight(highlight) {
        this.highlightManager.renderHighlight(highlight, this.getNodesInRange.bind(this));
    }

    renderHighlightInPage(context, startOffset, length, className, color, highlight) {
        this.highlightManager.renderHighlightInPage(
            context, startOffset, length, className, color, highlight, this.onSelectHighlight
        );
    }

    renderHighlightInPageWhenIsSearching(context, queryWords, frameIsLoading) {
        this.highlightManager.renderHighlightInPageWhenIsSearching(context, queryWords, frameIsLoading);
    }

    applyColorToMarker(mark, color) {
        this.highlightManager.applyColorToMarker(mark, color);
    }

    clearMarkedSelection(className, context) {
        this.highlightManager.clearMarkedSelection(className, context);
    }

    async saveHighlight(params, selection, anonymousSession) {
        await this.highlightManager.saveHighlight(
            params, selection, anonymousSession, this.getNodesInRange.bind(this), this.onSelectHighlight
        );
    }

    removeHighlight(selectionUniqueCssClass, context) {
        this.highlightManager.removeHighlight(selectionUniqueCssClass, context);
    }

    getCurrentChapterHighlights() {
        return this.highlightManager.getCurrentChapterHighlights();
    }

    setCurrentChapterHighlights(highlights) {
        this.highlightManager.setCurrentChapterHighlights(highlights);
    }

    resetInitHighlightsRetryCount() {
        this.highlightManager.resetInitHighlightsRetryCount();
    }

    clearMarkerSearchTimeout() {
        this.highlightManager.clearMarkerSearchTimeout();
    }

    destroy() {
        const hadOffscreenHandler = !!this._offscreenTimeoutHandler;
        const hadPagebreakTracker = !!this._pagebreakTracker;
        const hadHighlightManager = !!this.highlightManager;
        const idpfBlobUrlsRevoked = this.idpfFontBlobUrls?.size ?? 0;

        // Remove the global "Offscreen load timeout:" rejection listener.
        // See defense-in-depth comment in the init block where it's registered.
        if (this._offscreenTimeoutHandler) {
            window.removeEventListener('unhandledrejection', this._offscreenTimeoutHandler);
            this._offscreenTimeoutHandler = null;
        }
        this._pagebreakTracker?.destroy();
        this._pagebreakTracker = null;
        this.highlightManager?.destroy();

        // ─── IDPF Font Deobfuscation cleanup ───────────────────────────────
        if (this.idpfFontBlobUrls) {
            for (const url of this.idpfFontBlobUrls.values()) {
                URL.revokeObjectURL(url);
            }
            this.idpfFontBlobUrls = null;
        }
        // ───────────────────────────────────────────────────────────────────

        sendBreadcrumb('lifecycle', 'epub_viewer_destroyed', {
            hadOffscreenHandler,
            hadPagebreakTracker,
            hadHighlightManager,
            idpfBlobUrlsRevoked,
        });
    }
}

/**
 * HighlightManager - Manages all highlight-related functionality for EPUB content
 *
 * @class HighlightManager
 */
export class HighlightManager {
    constructor({
        onHighlightCreated = () => {},
        onHighlightUpdated = () => {},
        onHighlightDeleted = () => {},
        onSearchHighlightRendered = () => {},
        onTrackEvent = () => {},
        onSetCurrentHighlight = () => {},
        onFulfillQueryWordsHighlight = () => {},
        onHandleAnonymousSessionSelections = () => {},
        onSelectHighlight = () => {},
        onSelectionInProgress = () => {}, // @R2 selection popup
        onClearSelection = () => {}, // @R2 dismiss selection popup
    } = {}) {
        this.currentChapterHighlights = {};
        this.initHighlightsRetryCount = 0;
        this.markerSearch = null;

        this.onHighlightCreated = onHighlightCreated;
        this.onHighlightUpdated = onHighlightUpdated;
        this.onHighlightDeleted = onHighlightDeleted;
        this.onSearchHighlightRendered = onSearchHighlightRendered;
        this.onTrackEvent = onTrackEvent;
        this.onSetCurrentHighlight = onSetCurrentHighlight;
        this.onFulfillQueryWordsHighlight = onFulfillQueryWordsHighlight;
        this.onHandleAnonymousSessionSelections = onHandleAnonymousSessionSelections;
        this.onSelectHighlight = onSelectHighlight;
        this.onSelectionInProgress = onSelectionInProgress; // @R2
        this.onClearSelection = onClearSelection; // @R2
    }

    /**
     * Initialize highlights for the current chapter.
     * Uses a getter function to access current Vuex state on each retry,
     * avoiding the stale-closure bug where primitive values were captured by value.
     *
     * @param {Function} getSelectionsState - Returns { fetched: boolean, selections: Array }
     * @param {Array} context - Chapter context items with bodyDomNode and epubChapter
     * @param {Function} getNodesInRange - Helper to get nodes in a Range
     */
    initHighlights(getSelectionsState, context, getNodesInRange) {
        const RETRY_INTERVAL_MS = 50;
        const MAX_RETRIES = 50;

        this.context = context;

        const { fetched, selections } = getSelectionsState();
        if (fetched) {
            this.updateCurrentChapterHighlights(selections, context);
            this.renderHighlights(getNodesInRange);
        } else if (this.initHighlightsRetryCount < MAX_RETRIES) {
            setTimeout(() => {
                this.initHighlightsRetryCount++;
                this.initHighlights(getSelectionsState, context, getNodesInRange);
            }, RETRY_INTERVAL_MS);
        } else {
            sendBreadcrumb('selection', 'highlight_render_failed', {
                chapterHref: this.currentChapterHref,
                retries: this.initHighlightsRetryCount,
            }, 'warning');
        }
    }

    /**
     * Normalize a chapter reference for comparison.
     * R1 stored epubChapter as spineItem.idref (e.g., "book2"),
     * while R2 uses currentLocation.href (e.g., "book2.xhtml" or "OEBPS/book2.xhtml").
     * Strips path and extension so both formats match.
     *
     * @param {string} ref - Chapter reference (idref or href)
     * @returns {string} Normalized reference without path or extension
     */
    normalizeChapterRef(ref) {
        if (!ref) return '';
        // "OEBPS/book2.xhtml" → "book2", "book2" → "book2"
        return ref.replace(/^.*\//, '').replace(/\.[^.]+$/, '');
    }

    updateCurrentChapterHighlights(selections, context) {
        this.currentChapterHighlights = {};

        // @R2 Build fresh context from Readium's current content frames (_cframes)
        // instead of using the accumulated Vuex context array, which may contain
        // references to destroyed DOM nodes from previous chapter navigations.
        let contextToUse = context;
        if (this.getContentFrames) {
            const cframes = this.getContentFrames();
            if (cframes.length > 0) {
                const freshContext = cframes.map(cframe => {
                    try {
                        const doc = cframe?.window?.document;
                        const body = doc?.body;
                        // R2 injects <base data-readium href="..."> with the chapter href
                        const baseEl = doc?.querySelector('base[data-readium]');
                        const fullHref = baseEl?.getAttribute('href') || '';

                        return {
                            bodyDomNode: body,
                            epubChapter: fullHref
                        };
                    } catch (e) {
                        return null;
                    }
                }).filter(c => c && c.bodyDomNode);

                if (freshContext.length > 0) {
                    contextToUse = freshContext;
                }
            }
        }

        contextToUse.map((contextItem) => {
            const contextChapterNorm = this.normalizeChapterRef(contextItem.epubChapter);
            const contextBodyId = contextItem.bodyDomNode?.id || '';
            selections
                .filter((highlight) => {
                    const highlightChapterNorm = this.normalizeChapterRef(highlight.epubChapter);

                    // 1. Direct normalized comparison (handles R1 "book2" vs R2 "book2.xhtml")
                    if (highlightChapterNorm === contextChapterNorm) {
                        return true;
                    }

                    // 2. Body DOM id fallback
                    if (highlight.epubChapter === contextBodyId) {
                        return true;
                    }

                    // 3. @R2 idref→href resolution: R1 stored OPF item id (e.g. "book2")
                    //    which maps to an href (e.g. "GeographyofBli_body_split_002.html")
                    //    via the content.opf manifest.
                    if (this.idrefToHrefMap) {
                        const resolvedHref = this.idrefToHrefMap.get(highlight.epubChapter);
                        if (resolvedHref) {
                            const resolvedNorm = this.normalizeChapterRef(resolvedHref);
                            if (resolvedNorm === contextChapterNorm) {
                                return true;
                            }
                        }
                    }

                    return false;
                })
                .map((highlight) => {
                    highlight.isRendered = false;
                    highlight.contextBodyDomNode = contextItem.bodyDomNode;
                    highlight.epubChapter = contextItem.epubChapter;
                    this.currentChapterHighlights[highlight.uuid] = highlight;
                });
        });

    }

    renderHighlights(getNodesInRange) {
        const highlights = Object.values(this.currentChapterHighlights);
        if (highlights.length === 0) {
            // @R2 Even without highlights, setup selection listeners for new highlight creation
            this._setupSelectionListeners();
            return;
        }

        // @R2 Content readiness check: R2 may fire frameLoaded with a placeholder body
        // (e.g. 16 chars) while the real decrypted content lives in a different iframe.
        // When the cached body isn't ready, scan all viewport iframes to find the real one.
        if (!this._isBodyContentReady(highlights)) {
            const realBody = this._findContentBody(highlights);

            if (realBody) {
                highlights.forEach(h => { h.contextBodyDomNode = realBody; });
            } else {
                if (!this._bodyReadyRetryCount) this._bodyReadyRetryCount = 0;
                if (this._bodyReadyRetryCount < 20) {
                    this._bodyReadyRetryCount++;
                    setTimeout(() => this.renderHighlights(getNodesInRange), 500);
                    return;
                }
                this._bodyReadyRetryCount = 0;
                return;
            }
        }
        this._bodyReadyRetryCount = 0;

        highlights.forEach(highlight => this.renderHighlight(highlight, getNodesInRange));

        // @R2 After initial render, watch for the cframe body (the ACTUAL visible frame)
        // to get real content. The initial render may be on a fallback viewport iframe
        // which is not the primary display — marks appear but mispositioned.
        // When the cframe body is ready, re-render there for correct positioning.
        this._watchForCframeRerender(getNodesInRange);

        // @R2 Setup text selection detection on content frames
        this._setupSelectionListeners();
    }

    /**
     * @R2 Watch for R2's content frame body (_cframes) to get real content,
     * then re-render highlights there for correct visual positioning.
     *
     * R2's _cframes are the actual visible rendering frames, but they may start
     * with placeholder content (~16 chars) while decryption completes. When the
     * cframe body gets real content, re-render highlights there and clear marks
     * from the old (fallback) body.
     */
    _watchForCframeRerender(getNodesInRange) {
        if (this._cframeWatcherId) {
            clearInterval(this._cframeWatcherId);
            this._cframeWatcherId = null;
        }

        if (!this.getContentFrames) return;

        const highlights = Object.values(this.currentChapterHighlights);
        if (highlights.length === 0) return;

        const currentBody = highlights[0].contextBodyDomNode;
        const sampleText = this._getSampleTextForValidation(highlights);

        // Check if we're already on a cframe body — no need to watch
        const cframesNow = this.getContentFrames() || [];
        for (const cframe of cframesNow) {
            try {
                if (cframe?.window?.document?.body === currentBody) {
                    return;
                }
            } catch (e) {}
        }

        let attempts = 0;
        this._cframeWatcherId = setInterval(() => {
            attempts++;
            if (attempts > 30) { // 15 seconds
                clearInterval(this._cframeWatcherId);
                this._cframeWatcherId = null;
                return;
            }

            const cframes = this.getContentFrames() || [];
            for (const cframe of cframes) {
                try {
                    const body = cframe?.window?.document?.body;
                    const textLen = body?.textContent?.length || 0;

                    if (textLen > 100 && (!sampleText || body.textContent.includes(sampleText))) {
                        sendBreadcrumb('frame', 'cframe_rerender_detected', {
                            chapterHref: this.currentChapterHref,
                        });
                        // Unmark old body
                        const oldHighlights = Object.values(this.currentChapterHighlights);
                        for (const h of oldHighlights) {
                            try {
                                const oldMarker = new Mark(h.contextBodyDomNode);
                                oldMarker.unmark({ className: h.getCssClassForMarker() });
                            } catch (e) {}
                        }

                        // Re-render on cframe body
                        for (const h of oldHighlights) {
                            h.isRendered = false;
                            h.contextBodyDomNode = body;
                        }
                        oldHighlights.forEach(h => this.renderHighlight(h, getNodesInRange));

                        // @R2 Re-setup selection listeners on the new cframe
                        this._setupSelectionListeners();

                        clearInterval(this._cframeWatcherId);
                        this._cframeWatcherId = null;
                        return;
                    }

                } catch (e) {}
            }
        }, 500);
    }

    /**
     * @R2 Setup text selection detection on content frames.
     * Adds pointerup listeners to detect text selection since R2's native
     * textSelected event may not fire reliably in all iframe configurations.
     * Calls onSelectionInProgress (same as R2's textSelected flow) to show the popup.
     */
    _setupSelectionListeners() {
        // Clean up previous listeners
        if (this._selectionCleanups) {
            this._selectionCleanups.forEach(fn => fn());
            this._selectionCleanups = null;
        }

        const cleanups = [];
        const bodiesWithListeners = new Set();

        // Collect all frames that might contain visible content
        const frameDocs = [];

        // 1. cframes (primary content frames the user sees)
        if (this.getContentFrames) {
            const cframes = this.getContentFrames() || [];
            for (const cframe of cframes) {
                try {
                    const doc = cframe?.window?.document;
                    const body = doc?.body;
                    if (doc && body && body.textContent?.length > 50 && !bodiesWithListeners.has(body)) {
                        frameDocs.push({ doc, frameElement: doc.defaultView?.frameElement });
                        bodiesWithListeners.add(body);
                    }
                } catch (e) {}
            }
        }

        // 2. Viewport iframes (fallback)
        const allBodies = this._collectAllIframeBodies();
        for (const { body } of allBodies) {
            try {
                if (body.textContent?.length > 50 && !bodiesWithListeners.has(body)) {
                    const doc = body.ownerDocument;
                    const frameElement = doc.defaultView?.frameElement;
                    if (doc && frameElement) {
                        frameDocs.push({ doc, frameElement });
                        bodiesWithListeners.add(body);
                    }
                }
            } catch (e) {}
        }

        const POINTERUP_SETTLE_MS = 10;
        const SELECTIONCHANGE_CLEAR_MS = 50;
        const ANDROID_SELECTION_SETTLE_MS = 250;
        const onAndroid = isAndroid();

        // anchorNode/range nodes can be Text nodes — resolve to the nearest element
        // before calling .closest() / inspecting attributes.
        const asElement = (node) => node?.nodeType === Node.ELEMENT_NODE ? node : node?.parentElement;
        const isInsideMark = (node) => !!asElement(node)?.closest?.('mark');

        for (const { doc, frameElement } of frameDocs) {
            const emitCurrentSelection = () => {
                const sel = doc.defaultView?.getSelection();
                const text = sel?.toString() ?? '';
                if (!text.trim()) return false;

                const range = sel.getRangeAt(0);
                const rects = range.getClientRects();
                if (!rects || rects.length === 0) return false;

                // Tapping a `<mark>` auto-selects its text on Android; the
                // highlight's own click handler owns that flow.
                if (isInsideMark(range.commonAncestorContainer)) return false;

                this.onSelectionInProgress({
                    text,
                    position: { x: rects[0].x, y: rects[0].y, width: rects[0].width, height: rects[0].height },
                    iframeDocument: frameElement,
                    range,
                    startContainer: range.startContainer,
                    endContainer: range.endContainer,
                    startOffset: range.startOffset,
                    endOffset: range.endOffset,
                    commonAncestorContainer: range.commonAncestorContainer,
                    parentElements: {
                        startParent: asElement(range.startContainer),
                        endParent: asElement(range.endContainer),
                        commonParent: asElement(range.commonAncestorContainer),
                    },
                });
                return true;
            };

            const pointerUpHandler = (e) => {
                if (e.target?.closest?.('mark')) return;
                setTimeout(() => {
                    try {
                        const sel = doc.defaultView?.getSelection();
                        if (!sel?.toString().trim()) {
                            this.onClearSelection();
                            return;
                        }
                        emitCurrentSelection();
                    } catch {}
                }, POINTERUP_SETTLE_MS);
            };
            doc.addEventListener('pointerup', pointerUpHandler);
            cleanups.push(() => doc.removeEventListener('pointerup', pointerUpHandler));

            // Catches selection clears that pointerup misses (click outside the
            // iframe, Escape, programmatic clear).
            let clearSelectionTimer = null;
            const selectionChangeHandler = () => {
                clearTimeout(clearSelectionTimer);
                clearSelectionTimer = setTimeout(() => {
                    try {
                        const sel = doc.defaultView?.getSelection();
                        if (sel?.toString().trim()) return;
                        // Caret inside a `<mark>` means the user just tapped a
                        // highlight; clearing now would close its popup mid-open.
                        if (isInsideMark(sel?.anchorNode)) return;
                        this.onClearSelection();
                    } catch {
                        // Cross-origin frames will throw; safe to ignore.
                    }
                }, SELECTIONCHANGE_CLEAR_MS);
            };
            doc.addEventListener('selectionchange', selectionChangeHandler);
            cleanups.push(() => {
                clearTimeout(clearSelectionTimer);
                doc.removeEventListener('selectionchange', selectionChangeHandler);
            });

            // Android long-press release doesn't fire `pointerup` (the system
            // selection UI captures the gesture). Use stabilized selectionchange
            // as the "selection finished" signal.
            if (onAndroid) {
                let androidEmitTimer = null;
                const androidSelectionEmit = () => {
                    clearTimeout(androidEmitTimer);
                    androidEmitTimer = setTimeout(() => {
                        try { emitCurrentSelection(); } catch {}
                    }, ANDROID_SELECTION_SETTLE_MS);
                };
                doc.addEventListener('selectionchange', androidSelectionEmit);
                cleanups.push(() => {
                    clearTimeout(androidEmitTimer);
                    doc.removeEventListener('selectionchange', androidSelectionEmit);
                });
            }
        }

        this._selectionCleanups = cleanups;
    }

    /**
     * @R2 Check if the body has enough text content for legacy highlights to render.
     *
     * R2 fires frameLoaded with a placeholder body (~16 chars) before the real
     * decrypted content is available (in a separate iframe). This checks:
     * - For ALL highlights: body must have real content (> 50 chars), not a placeholder.
     * - For legacy highlights: body text must be long enough for the largest char offset.
     *
     * @param {Array} highlights - Highlights to check against
     * @returns {boolean} true if body content is sufficient for rendering
     */
    _isBodyContentReady(highlights) {
        const body = highlights[0]?.contextBodyDomNode;
        if (!body) return false;

        const bodyTextLength = body.textContent?.length || 0;

        // @R2 Placeholder detection: R2 placeholder bodies have minimal content (~16 chars).
        // Both R2 and legacy highlights need the real body to render.
        if (bodyTextLength <= 50) return false;

        // For legacy highlights, verify body has enough text for the largest offset
        const maxNeeded = this._getMaxNeededTextLength(highlights);
        if (maxNeeded > 0 && bodyTextLength < maxNeeded) return false;

        return true;
    }

    /**
     * @R2 Calculate the minimum body text length needed for legacy highlights.
     * @param {Array} highlights - All highlights (legacy will be filtered)
     * @returns {number} Max character offset + length needed across all legacy highlights
     */
    _getMaxNeededTextLength(highlights) {
        const legacyHighlights = highlights.filter(h => h.isLegacy?.());
        if (legacyHighlights.length === 0) return 0;
        return Math.max(...legacyHighlights.map(h =>
            (Number(h.epubLocationStartChar) || 0) + (h.totalText?.length || h.totalLength || 0)
        ));
    }

    /**
     * @R2 Find the content body that contains the highlight text.
     *
     * R2 may deliver onLoadFrame with a placeholder body while the real decrypted
     * content lives in a different iframe. This method searches for a body that
     * actually contains the highlight text (not just any body with enough chars,
     * which could be the table of contents or a different chapter).
     *
     * Search order:
     * 1. R2 navigator content frames (_cframes) — the visible rendering frames
     * 2. All iframes in #readiumViewport (flat scan)
     * 3. Nested iframes (iframes within iframes) — R2 may use deep nesting
     *
     * @param {Array} highlights - Highlights to validate body content against
     * @returns {HTMLElement|null} The body element containing highlight text, or null
     */
    _findContentBody(highlights) {
        const MIN_REAL_BODY_LENGTH = 100;
        const maxNeeded = Math.max(this._getMaxNeededTextLength(highlights), MIN_REAL_BODY_LENGTH);

        // @R2 Get a sample text snippet to validate we found the RIGHT chapter,
        // not just any iframe with enough characters (e.g. the table of contents).
        const sampleText = this._getSampleTextForValidation(highlights);

        const isValidBody = (body) => {
            const textLen = body?.textContent?.length || 0;
            if (textLen < maxNeeded) return false;
            // Validate body actually contains highlight text (not a different chapter)
            if (sampleText && !body.textContent.includes(sampleText)) return false;
            return true;
        };

        // 1. Primary: use R2's navigator content frames (the visible ones)
        if (this.getContentFrames) {
            const cframes = this.getContentFrames();
            for (const cframe of cframes) {
                try {
                    const body = cframe?.window?.document?.body;
                    const textLen = body?.textContent?.length || 0;
                    if (body && isValidBody(body)) {
                        return body;
                    }
                } catch (e) {
                    // Cross-origin or unavailable frame
                }
            }
        }

        // 2. Scan all iframes in the viewport (flat + nested)
        const candidateBodies = this._collectAllIframeBodies();

        for (const { body, source, depth } of candidateBodies) {
            if (isValidBody(body)) {
                return body;
            }
        }

        return null;
    }

    /**
     * @R2 Get a short text snippet from highlights to validate the correct chapter body.
     * Uses the shortest highlight text (fastest to search) and picks a middle fragment
     * to avoid whitespace issues at boundaries.
     *
     * @param {Array} highlights - Highlights to extract sample from
     * @returns {string|null} A text snippet to search for, or null
     */
    _getSampleTextForValidation(highlights) {
        let shortest = null;
        for (const h of highlights) {
            const text = h.totalText?.replace(/\n/g, '');
            if (text && text.length >= 8) {
                if (!shortest || text.length < shortest.length) {
                    shortest = text;
                }
            }
        }
        if (!shortest) return null;

        // Use a middle fragment (avoids edge whitespace issues)
        if (shortest.length > 20) {
            const start = Math.floor(shortest.length * 0.2);
            return shortest.substring(start, start + 30);
        }
        return shortest;
    }

    /**
     * @R2 Collect body elements from all accessible iframes, including nested ones.
     * R2 may use deeply nested iframes for rendering decrypted EPUB content.
     *
     * @returns {Array<{body: HTMLElement, source: string, depth: number}>}
     */
    _collectAllIframeBodies() {
        const results = [];

        const scanIframes = (root, depth, sourcePath) => {
            let iframes;
            try {
                iframes = root.querySelectorAll('iframe');
            } catch (e) {
                return;
            }
            for (const iframe of iframes) {
                try {
                    const body = iframe.contentDocument?.body;
                    if (body) {
                        const src = iframe.src?.substring(0, 60) || iframe.id || '(anon)';
                        results.push({ body, source: `${sourcePath}>${src}`, depth });
                        // Recurse into nested iframes
                        if (depth < 4) {
                            scanIframes(iframe.contentDocument, depth + 1, `${sourcePath}>${src}`);
                        }
                    }
                } catch (e) {
                    // Cross-origin iframe, skip
                }
            }
        };

        // Start from the viewport element
        const viewport = document.getElementById('readiumViewport');
        if (viewport) {
            scanIframes(viewport, 0, '#readiumViewport');
        }

        // Also scan from document root (in case content iframes are outside the viewport)
        scanIframes(document, 0, 'document');

        // Deduplicate (same body from different paths)
        const seen = new Set();
        return results.filter(r => {
            if (seen.has(r.body)) return false;
            seen.add(r.body);
            return true;
        });
    }

    renderHighlight(highlight, getNodesInRange) {
        if (highlight.isRendered) {
            const renderedHighlightMarks = highlight.contextBodyDomNode.querySelectorAll(
                `.${highlight.getUniqueCssClass()}`
            );
            if (renderedHighlightMarks.length === 0) return;
            for (const mark of renderedHighlightMarks) {
                this.applyColorToMarker(mark, highlight.color);
            }
        } else {
            if(highlight.isLegacy()) {
                // @R2 Legacy highlights: use text search (most robust for R1 → R2 rendering).
                // Text search finds the actual text in the DOM regardless of character offset
                // counting differences between R1 and R2, or DOM structure variations.
                // @R2 Lazy migration: compute R2 location BEFORE rendering
                // (Mark.js inserts <mark> tags that alter text node structure)
                let r2Location = null;
                if (!highlight.epubR2Location && highlight.totalText && highlight.persistR2Migration) {
                    r2Location = this.convertLegacyToR2(highlight);
                }

                const textSuccess = this.renderHighlightByText(highlight);

                if (textSuccess) {
                    // @R2 Lazy migration: persist full R2 location to DB only if conversion succeeded.
                    // If convertLegacyToR2 failed, keep as R1 so it retries on next load.
                    if (!highlight.epubR2Location && highlight.totalText && highlight.persistR2Migration && r2Location) {
                        highlight.persistR2Migration(r2Location);
                    }
                } else {
                    // Text search failed — try Mark.js markRanges with char offsets as last resort
                    const startCharOffset = Number(highlight.epubLocationStartChar) || 0;
                    this.renderHighlightInPage(
                        highlight.contextBodyDomNode,
                        startCharOffset,
                        highlight.totalLength,
                        highlight.getCssClassForMarker(),
                        highlight.color,
                        highlight
                    );
                }
            } else {
                // @R2 path: try CSS selector rendering first (works for native R2 highlights)
                const r2Success = this.renderHighlightR2(highlight, getNodesInRange);

                if (!r2Success) {
                    // @R2 CSS selectors failed → fall back to text search
                    this.renderHighlightByText(highlight);
                }
            }
        }
    }

    /**
     * @R2 Render a highlight using the R2 CSS selector-based path.
     * Extracted from renderHighlight() for reuse by both native R2 and migrated R1 highlights.
     * @returns {boolean} true if rendering succeeded, false if selectors couldn't be resolved
     */
    renderHighlightR2(highlight, getNodesInRange) {
        if(!highlight.location?.selection?.startCss || !highlight.location?.selection?.endCss) {
            return false;
        }

        const body = highlight.contextBodyDomNode;
        const doc = body.ownerDocument;
        const range = doc.createRange();

        function findFirstTextNode(el) {
            const walker = el.ownerDocument.createTreeWalker(
                el,
                NodeFilter.SHOW_TEXT,
                (node) => {
                    return node.textContent.length > 0 ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP;
                }
            );
            return walker.nextNode();
        }

        const { startCss, endCss, startOffset, endOffset, startElementOffset } = highlight.location.selection;

        const startElement = body.querySelector(startCss);
        if (!startElement) {
            return false;
        }
        const startNode = findFirstTextNode(startElement) || startElement;

        const endElement = body.querySelector(endCss);
        if (!endElement) {
            return false;
        }
        const endNode = findFirstTextNode(endElement) || endElement;

        try {
            range.setStart(startNode, startOffset);
            range.setEnd(endNode, endOffset);
        } catch (e) {
            return false;
        }

        const rangeText = range.toString();
        const nodesInRange = getNodesInRange(range, doc);

        this.renderHighlightInPage(
            nodesInRange,
            startElementOffset,
            highlight.totalLength,
            highlight.getCssClassForMarker(),
            highlight.color,
            highlight
        );
        return true;
    }

    /**
     * @R2 Render a highlight by searching for its text content in the DOM.
     *
     * More robust than CSS selector-based rendering — finds the text regardless
     * of DOM structure or iframe source. Uses Mark.js mark() with acrossElements
     * to handle text spanning multiple HTML elements (paragraphs, spans, etc.).
     *
     * @param {Object} highlight - Highlight with totalText and contextBodyDomNode
     * @returns {boolean} true if at least one mark was created
     */
    renderHighlightByText(highlight) {
        const body = highlight.contextBodyDomNode;
        if (!body || !highlight.totalText) {
            return false;
        }

        // Normalize: remove newlines — DOM element boundaries have no character separator
        // in textContent, but R1 stored newlines for visual line breaks.
        const searchText = highlight.totalText.replace(/\n/g, '');

        if (searchText.length < 2) {
            return false;
        }

        const className = highlight.getCssClassForMarker();
        const Marker = new Mark(body);

        Marker.unmark({ className });

        let markCount = 0;

        Marker.mark(searchText, {
            acrossElements: true,
            separateWordSearch: false,
            className: className,
            each: (mark) => {
                markCount++;
                mark.addEventListener('click', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    this.onSelectHighlight(highlight);
                });
                mark.addEventListener('tap', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    this.onSelectHighlight(highlight);
                });
                this.applyColorToMarker(mark, highlight.color);
                this.applyIconToAnnotations(body, className, highlight.hasAnnotation);
                highlight.isRendered = true;
            },
            noMatch: () => {
            },
            done: () => {
            },
        });

        return markCount > 0;
    }

    /**
     * @R2 Lazy migration: Convert an R1 legacy highlight to R2 format.
     *
     * Walks text nodes in the body using the body-level character offset (epubLocationStartChar)
     * to locate the start and end positions, then generates CSS selectors for the parent elements.
     * Also extracts navigation data (href, progression) for R2 Locator-based navigation.
     *
     * @param {Object} highlight - Legacy highlight with epubLocationStartChar and totalText
     * @returns {Object|null} EpubR2Location with selection and navigation data, or null if failed
     */
    convertLegacyToR2(highlight) {
        try {
            const body = highlight.contextBodyDomNode;
            const doc = body.ownerDocument;
            const startCharOffset = Number(highlight.epubLocationStartChar) || 0;

            // R1 replaces \n with __ for length (2 chars per newline instead of 1).
            // This matches how Mark.js counts characters in the DOM.
            const adjustedLength = highlight.totalText.split('\n').join('__').length;
            const endCharOffset = startCharOffset + adjustedLength;

            // Walk text nodes sequentially (same counting as Mark.js markRanges)
            const walker = doc.createTreeWalker(body, NodeFilter.SHOW_TEXT, null);
            let currentOffset = 0;
            let node;
            let startNode = null, startNodeOffset = 0, startParent = null;
            let endNode = null, endNodeOffset = 0, endParent = null;
            let textNodeCount = 0;

            while ((node = walker.nextNode())) {
                const nodeLength = node.textContent.length;
                textNodeCount++;

                if (!startNode && currentOffset + nodeLength > startCharOffset) {
                    startNode = node;
                    startNodeOffset = startCharOffset - currentOffset;
                    startParent = node.parentElement;
                }

                if (currentOffset + nodeLength >= endCharOffset) {
                    endNode = node;
                    endNodeOffset = endCharOffset - currentOffset;
                    endParent = node.parentElement;
                    break;
                }

                currentOffset += nodeLength;
            }

            if (!startNode || !endNode || !startParent || !endParent) {
                return null;
            }

            // Generate CSS selectors relative to body
            const selectorOptions = {
                root: body,
                combineBetweenSelectors: false,
                selectors: ['id', 'tag', 'class', 'attribute', 'nthchild', 'nthoftype'],
            };

            const startCss = getCssSelector(startParent, selectorOptions);
            const endCss = getCssSelector(endParent, selectorOptions);

            if (!startCss || !endCss) {
                return null;
            }

            // Calculate startElementOffset: char offset from start of startParent to selection start
            const measureRange = doc.createRange();
            measureRange.selectNodeContents(startParent);
            measureRange.setEnd(startNode, startNodeOffset);
            const startElementOffset = measureRange.toString().length;

            // Extract href from the iframe's <base> tag (injected by R2)
            // Use getRelativeHref to convert full URL to publication-relative path
            const baseEl = doc.querySelector('base[data-readium]');
            const fullHref = baseEl?.getAttribute('href') || '';
            const href = this.getRelativeHref(fullHref) || '';

            // Calculate progression within the chapter (0-1 range)
            const bodyTextLength = body.textContent?.length || 1;
            const progression = Math.min(1, Math.max(0, startCharOffset / bodyTextLength));

            return {
                // Selection data (for rendering)
                startCss,
                endCss,
                startElementOffset,
                startOffset: startNodeOffset,
                endOffset: endNodeOffset,
                text: highlight.totalText,
                // Navigation data (for R2 Locator)
                href,
                type: 'application/xhtml+xml',
                locations: {
                    progression,
                },
            };
        } catch (error) {
            console.warn('@R2 Lazy migration failed for highlight', highlight.uuid, error);
            return null;
        }
    }

    /**
     * @R2 Last-resort fallback: render a highlight using Mark.js markRanges (character offsets).
     * Used when both CSS selector rendering (renderHighlightR2) and text search rendering
     * (renderHighlightByText) fail. Uses R1's epubLocationStartChar body-level offset to
     * locate the highlight in the DOM via Mark.js character counting.
     *
     * This method is less reliable than text search because character offset counting can
     * differ between R1 and R2 due to DOM structure variations.
     *
     * @param {HTMLElement} context - The body DOM node to render within
     * @param {number} startOffset - Character offset from start of body
     * @param {number} length - Length of highlighted text
     * @param {string} className - CSS class for the mark elements
     * @param {string} color - Highlight color
     * @param {Object} highlight - The highlight model object
     */
    renderHighlightInPage(context, startOffset, length, className, color, highlight) {
        if(!context) {
            return;
        }

        const computedLength = highlight.totalText.split("\n").join(highlight.isLegacy() ? "__" : "").length;

        const Marker = new Mark(context);

        Marker.unmark({className: className});

        let markCount = 0;
        Marker.markRanges(
            [
                {
                    start: startOffset,
                    length: computedLength,
                }
            ],
            {
                className: className,
                debug: true,
                each: (mark) => {
                    markCount++;
                    mark.addEventListener('click', (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        this.onSelectHighlight(highlight);
                    });
                    mark.addEventListener('tap', (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        this.onSelectHighlight(highlight);
                    });
                    this.applyColorToMarker(mark, color);
                    this.applyIconToAnnotations(highlight.contextBodyDomNode, className, highlight.hasAnnotation);
                    highlight.isRendered = true;
                },
                noMatch: (range) => {
                },
                done: () => {
                },
            }
        );
    }

    renderHighlightInPageWhenIsSearching(context, queryWords, frameIsLoading) {
        clearTimeout(this.markerSearch);

        if (frameIsLoading) {
            this.markerSearch = setTimeout(() => {
                this.renderHighlightInPageWhenIsSearching(context, queryWords, frameIsLoading);
            }, 1000);
            return;
        }

        const className = {className: 'search'};
        const color = '#0fff13';

        context.map((contextItem) => {
            const Marker = new Mark(contextItem.bodyDomNode);
            Marker.unmark(className);
            Marker.mark(queryWords, {
                ...className,
                accuracy: 'partially',
                each: (mark) => {
                    mark.style.setProperty('background-color', hexToRgba(color, 0.3), 'important');
                },
            });
        });

        this.onFulfillQueryWordsHighlight();
    }

    applyColorToMarker(mark, color) {
        mark.style.setProperty('background-color', hexToRgba(color, 0.3), 'important');
        mark.style.cursor = 'pointer';
    }

    removeHighlight(className, context) {
        sendBreadcrumb('selection', 'highlight_removed', {
            chapterHref: this.currentChapterHref,
        });
        context.map((contextItem) => {
            const Marker = new Mark(contextItem.bodyDomNode);
            Marker.unmark({className: className});
        });
        this.onHighlightDeleted();
    }

    applyIconToAnnotations(context, className, hasAnnotation) {
        if (!hasAnnotation) return;
        const firstAnnotation = context.querySelector(`.${className.split(' ')[0]}`);
        if (firstAnnotation.classList.contains('annotation-icon')) return;
        firstAnnotation.classList.add('annotation-icon');
    }

    async saveHighlight(params, selection, anonymousSession, getNodesInRange) {
        const newColor = params.color;
        const oldColor = selection.color;
        selection.color = newColor;

        sendBreadcrumb('selection', 'highlight_saved', {
            chapterHref: this.currentChapterHref,
            color: newColor,
            hasNote: !!(params.hasAnnotation && params.annotationText?.length),
        });

        let needUpdatedAnnotationText = false;
        if (params.hasAnnotation && params.annotationText.length !== 0) {
            selection.hasAnnotation = params.hasAnnotation;
            selection.annotationText = params.annotationText;
            needUpdatedAnnotationText = params.needUpdated;
        }

        this.renderHighlight(selection, getNodesInRange);

        if (anonymousSession) {
            this.onHandleAnonymousSessionSelections(selection, oldColor, newColor);
            this.onTrackEvent({
                action: 'create annon highlight',
                trigger: '',
                source: 'readium',
            });
            return;
        }

        if (!selection.confirmed) {
            await selection.store();
            this.onTrackEvent({
                action: 'create highlight',
                trigger: '',
                source: 'readium',
            });
        } else if (oldColor !== newColor || needUpdatedAnnotationText) {
            await selection.update();
            await selection.updateAnnotationText();
            this.onHighlightUpdated();
        }

        this.currentChapterHighlights[selection.uuid] = selection;
        this.onHighlightCreated(selection);
    }

    getCurrentChapterHighlights() {
        return this.currentChapterHighlights;
    }

    setCurrentChapterHighlights(highlights) {
        this.currentChapterHighlights = highlights;
    }

    resetInitHighlightsRetryCount() {
        this.initHighlightsRetryCount = 0;
    }

    clearMarkerSearchTimeout() {
        if (this.markerSearch) {
            clearTimeout(this.markerSearch);
            this.markerSearch = null;
        }
    }

    /**
     * Cleans up all timers, event listeners, and observer references.
     */
    destroy() {
        const hadCframeWatcher = !!this._cframeWatcherId;
        const selectionCleanupCount = this._selectionCleanups?.length ?? 0;
        const hadResizeObserver = !!this.epubNavigator?.resizeObserver;
        const hadFadeAnimator = !!this._fadeAnimator;

        if (this._cframeWatcherId) {
            clearInterval(this._cframeWatcherId);
            this._cframeWatcherId = null;
        }

        this.clearMarkerSearchTimeout();

        if (this._selectionCleanups) {
            this._selectionCleanups.forEach(fn => fn());
            this._selectionCleanups = null;
        }

        if (this.epubNavigator?.resizeObserver) {
            this.epubNavigator.resizeObserver.disconnect();
        }

        if (this._fadeAnimator) {
            this._fadeAnimator.cancel();
            this._fadeAnimator = null;
        }

        this.epubNavigator = null;
        this.publication = null;

        sendBreadcrumb('lifecycle', 'highlight_manager_destroyed', {
            hadCframeWatcher,
            selectionCleanupCount,
            hadResizeObserver,
            hadFadeAnimator,
        });
    }
}

/**
 * Custom HttpFetcher that handles content decryption
 */
// --- Readium file:// protocol patches ---
// Readium was designed for HTTP-served publications and strips "file://"
// from resolved URLs in two places that break local (file://) publications:
//
// 1. Link.toURL() strips file:// from the result (Link.ts line 166),
//    breaking blob-iframe <base> tags and fetch() calls.
// 2. FrameBlobBuilder injects a CSP meta tag using pub.baseURL as a source,
//    but file:///path is not a valid CSP source — browsers reject it,
//    blocking fonts/images in EPUB content frames.

// Patch 1: Preserve file:// in Link.toURL() when the base URL is file://.
const _originalLinkToURL = Link.prototype.toURL;
Link.prototype.toURL = function (baseUrl) {
    const result = _originalLinkToURL.call(this, baseUrl);
    if (result && baseUrl?.startsWith('file://') && !result.startsWith('file://')) {
        return `file://${result}`;
    }
    return result;
};

// Patch 2: Fix CSP in Readium's XHTML blobs — replace invalid file:///path
// sources with the valid file: scheme source so the browser allows loading
// local fonts, images, and other resources inside content iframes.
const _OriginalBlob = window.Blob;
const _cspMetaRe = /(http-equiv="Content-Security-Policy"[^>]*content=")(.*?)(")/;
const _fileUrlSourceRe = /file:\/\/\/[^\s;"]+/g;
window.Blob = class extends _OriginalBlob {
    constructor(parts, options) {
        if (
            options?.type?.includes('html') &&
            typeof parts?.[0] === 'string' &&
            parts[0].includes('data-readium="true"') &&
            parts[0].includes('file:///')
        ) {
            parts = [parts[0].replace(
                _cspMetaRe,
                (_, pre, csp, post) => pre + csp.replace(_fileUrlSourceRe, 'file:') + post
            )];
        }
        super(parts, options);
    }
};

// file:// fetch client shared across all DecryptingHttpFetcher instances.
// Normalizes status 0 responses (returned by fetch for file:// URLs) to
// status 200 so Readium's HttpResource resp.ok check passes.
const _fileClient = async (input, init) => {
    const resp = await fetch(input, init);
    if (resp.status === 0) {
        return new Response(resp.body, {
            status: 200,
            statusText: 'OK',
            headers: resp.headers,
        });
    }
    return resp;
};

class DecryptingHttpFetcher extends HttpFetcher {
    constructor(baseURL) {
        // HttpFetcher ctor signature: (fetchClient, baseURL).
        const client = baseURL?.startsWith('file://') ? _fileClient : undefined;
        super(client, baseURL);
        this.cssCache = null; // Populated externally after CSS pre-fetch
        // [IDPF] Populated externally with deobfuscated font blob URLs.
        this.idpfFontBlobUrls = null;
    }

    get(link) {
        const originalResource = super.get(link);

        const shouldDecrypt = ContentEncryptionService.shouldDecryptFile(link.href);

        if (shouldDecrypt) {
            return new DecryptingResource(originalResource, link.href, this.cssCache, this.idpfFontBlobUrls);
        }

        return originalResource;
    }
}

/**
 * Wrapper for Resource that decrypts content when read
 */
class DecryptingResource {
    constructor(originalResource, url, cssCache, idpfFontBlobUrls = null) {
        this.originalResource = originalResource;
        this.url = url;
        this.cssCache = cssCache;
        // [IDPF] Map<href, blobURL> for inline-style and external-CSS font rewrites.
        this.idpfFontBlobUrls = idpfFontBlobUrls;
    }

    _emitDecryptError(error) {
        sendBreadcrumb('fetch', 'decrypt_error', {
            href: this.url,
            errorName: error?.name,
            errorMessage: error?.message,
        }, 'error');
    }

    async link() {
        return await this.originalResource.link();
    }

    async length() {
        return await this.originalResource.length();
    }

    async read(range) {
        return await this.originalResource.read(range);
    }

    async close() {
        return await this.originalResource.close();
    }

    async readAsString(charset) {
        try {
            const encryptedText = await this.originalResource.readAsString(charset);

            if (this.isTextFile()) {
                const decryptedText = await Decrypter.decryptText(encryptedText);
                if (this.url.endsWith('.html') || this.url.endsWith('.xhtml') || this.url.endsWith('.htm') || this.url.endsWith('.xml') || this.url.endsWith('.ncx')) {
                    let html;
                    try {
                        html = fixXhtmlMarkup(decryptedText);
                    } catch (xhtmlError) {
                        sendBreadcrumb('fetch', 'xhtml_fix_error', {
                            href: this.url,
                            errorName: xhtmlError?.name,
                        }, 'warning');
                        throw xhtmlError;
                    }

                    // Inline pre-decrypted CSS directly into the HTML so the navigator's
                    // DOMParser sees <style> tags instead of <link> tags. This avoids the
                    // browser trying (and failing) to fetch encrypted CSS from the server
                    // when the blob-URL iframe loads.
                    html = this.inlineCssInHtml(html);

                    return html;
                }
                return decryptedText;
            }

            return encryptedText;
        } catch (error) {
            if (error?.status != null) {
                sendBreadcrumb('fetch', 'http_error', {
                    href: this.url,
                    status: error.status,
                }, 'error');
            } else {
                this._emitDecryptError(error);
            }
            console.warn('Failed to decrypt text content:', this.url, error);
            return await this.originalResource.readAsString(charset);
        }
    }

    /**
     * Replace <link rel="stylesheet" href="...css"> tags in HTML with inline <style>
     * tags containing pre-decrypted CSS from the shared cache.
     *
     * The CSS content is XML-escaped (&, <) so DOMParser can safely parse it
     * as XHTML. The browser decodes the entities when rendering.
     *
     * Also rebases relative url() references in the CSS so they resolve
     * correctly from the XHTML location rather than the original CSS location.
     */
    inlineCssInHtml(html) {
        // Directory of the XHTML file being processed (e.g. "OEBPS/")
        const xhtmlDir = dirFromPath(this.url);

        let result = html;

        if (this.cssCache && this.cssCache.size > 0) {
            result = result.replace(/<link\b[^>]*\/?>/gi, (match) => {
                // Only process stylesheet links
                if (!/\brel\s*=\s*["']stylesheet["']/i.test(match)) return match;

                const hrefMatch = match.match(/\bhref\s*=\s*["']([^"']+)["']/i);
                if (!hrefMatch) return match;

                const href = hrefMatch[1];
                if (!href.endsWith('.css')) return match;

                const cached = findCssInCacheWithKey(this.cssCache, href);
                if (cached) {
                    // Rebase url() references so they resolve from the XHTML directory
                    const rebasedCss = rebaseCssUrls(cached.css, cached.cacheKey, xhtmlDir);
                    // Escape for XHTML: & and < must be entity-encoded in element text content.
                    // The DOMParser decodes them back, and XMLSerializer re-encodes them,
                    // preserving round-trip correctness.
                    const xmlSafeCss = rebasedCss.replace(/&/g, '&amp;').replace(/</g, '&lt;');
                    return `<style type="text/css" data-original-href="${href}">${xmlSafeCss}</style>`;
                }

                return match;
            });
        }

        // [IDPF] Rewrite font url() references in any inline <style> blocks
        // (the cached-CSS rewrite already covered external stylesheets, but
        // @font-face declarations may also live inline in the XHTML and would
        // otherwise still point to the obfuscated font files).
        if (this.idpfFontBlobUrls && this.idpfFontBlobUrls.size > 0) {
            result = rewriteFontUrlsInText(result, this.idpfFontBlobUrls, xhtmlDir);
        }

        return result;
    }

    async readAsJSON() {
        try {
            const decryptedText = await this.readAsString();
            return JSON.parse(decryptedText);
        } catch (error) {
            this._emitDecryptError(error);
            console.warn('Failed to decrypt JSON content:', this.url, error);
            return await this.originalResource.readAsJSON();
        }
    }

    async readAsBlob() {
        try {
            if (this.isTextFile()) {
                const decryptedText = await this.readAsString();
                return new Blob([decryptedText], { type: this.getMimeType() });
            } else {
                const encryptedBlob = await this.originalResource.readAsBlob();
                const encryptedArrayBuffer = await encryptedBlob.arrayBuffer();
                const decryptedArrayBuffer = await Decrypter.decryptArrayBuffer(encryptedArrayBuffer);
                return new Blob([decryptedArrayBuffer], { type: this.getMimeType() });
            }
        } catch (error) {
            this._emitDecryptError(error);
            console.warn('Failed to decrypt blob content:', this.url, error);
            return await this.originalResource.readAsBlob();
        }
    }

    isTextFile() {
        return this.url.endsWith('.html') ||
               this.url.endsWith('.htm') ||
               this.url.endsWith('.xhtml') ||
               this.url.endsWith('.xml') ||
               this.url.endsWith('.css') ||
               this.url.endsWith('.js');
    }

    getMimeType() {
        if (this.url.endsWith('.html') || this.url.endsWith('.htm')) {
            return 'text/html';
        } else if (this.url.endsWith('.css')) {
            return 'text/css';
        } else if (this.url.endsWith('.js')) {
            return 'application/javascript';
        } else if (this.url.endsWith('.xhtml')) {
            return 'application/xhtml+xml';
        } else if (this.url.endsWith('.xml')) {
            return 'application/xml';
        } else if (this.url.endsWith('.json')) {
            return 'application/json';
        }
        return 'application/octet-stream';
    }
}
